import { Chess } from "@vendor/chess.js";
import { CONFIG } from "@js/config.js";
import { Engine } from "@js/engine.js";
import { Ranker } from "@js/ranker.js";
import { critique } from "@js/critique.js";
import { computePlanFit, computeRubric } from "@js/coaching.js";
import { AnalysisCoordinator, type EngineAnalyzeFn } from "../chess/analysisCoordinator.ts";
import {
  cpLossFromScores,
  parseEngineScore,
  scoreToComparable,
  toWhitePov,
  whiteMaterialCp,
  whitePovMate,
} from "../chess/score.ts";
import { START_FEN } from "../chess/positionKey.ts";
import { positionKey } from "../chess/positionKey.ts";
import { formatSampleLine, moveInWords } from "../chess/notation.ts";
import { SessionMachine } from "../session/machine.ts";
import {
  assessMove,
  formatAssessmentAxes,
  namedRepliesAt,
  replyFromGraph,
  replyFromNamedBranch,
  resolveTheory,
  sanitizeRecommendedSan,
  type TheoryResolution,
} from "../lessons/assessment.ts";
import type { LessonCourse, LessonNode, MoveAssessment } from "../lessons/schema.ts";
import { authoredObjective, pathToPosition, teachingMoveFor } from "../lessons/repository.ts";
import { loadCourseBySlug, openingRecord } from "../content/lookup.ts";
import { manifestByOpeningId } from "../content/manifest.ts";
import { buildTutorHintLadder, type HintDepth, type TutorHintStep } from "./hints.ts";
import { ideaForPosition, memoryCue } from "./memoryCue.ts";
import { selectReviewSet, soonCount } from "./reviewQueue.ts";
import {
  MAINLINES,
  bookCoachingAtPosition,
  type BookAlternative,
  type CoachingLayer,
} from "./bookCoaching.ts";
import { isBlackOpening, loadedBlackCourse } from "../content/blackCourses.ts";
import {
  reviewedContinuationAtPosition,
  reviewedReplyAtPosition,
} from "./reviewedContinuations.ts";
import {
  explainOffBookMove,
  moveConsequence,
  fallbackReply,
  positionGuideFor,
  structurePlanFor,
  type PositionGuide,
} from "./positionCoaching.ts";
import {
  MAX_PLIES,
  REPORT_CARD_EVERY,
  phaseForPlyCount,
  phaseGoalFor,
  whiteMoveNumber,
} from "./phases.ts";
import { qualityBandFromAssessment, type QualityBandId } from "./quality.ts";
import {
  buildReviewRecap,
  buildSessionRecap,
  type RecapStudentMove,
  type SessionRecap,
} from "./recap.ts";
import { readPosition, type PositionReading } from "./positionReading.ts";
import { heroSquares, reserveSquares } from "./boardOverlays.ts";
import { VET_DEPTH, hasEngineLines, rememberEngineLines } from "./engineLines.ts";
import { newlyAchievedThemes, themeStatuses, type ThemeStatus } from "./themes.ts";
import {
  buildReportCard,
  summarizeReportCard,
  type ReportCard,
  type ReportCardInput,
  type ReportCardMove,
  type UpcomingLesson,
} from "./reportCard.ts";
import { appendReportCardLog } from "./reportCardLog.ts";
import { bookCoachingFor } from "./bookCoaching.ts";
import {
  MEDAL_DEPTH,
  RANK_MAX_LINES,
  decideMedal,
  describeEngineChoices,
  legalLines,
  linesFromCandidates,
  lossFromLines,
  rankLines,
  medalLiveText,
  summarizeRanks,
  topEngineChoices,
  type EngineChoice,
  type MedalName,
  type MedalStatus,
  type RankedLine,
  type WithheldReason,
} from "./medals.ts";
import { loadReplyBriefings, packsFor } from "../content/replies/catalog.ts";
import { loadOpponentCategories, opponentCategory } from "./opponentCategory.ts";
import {
  graphReplyNote,
  replyForMove,
  replySoundness,
  replyTitle,
  withheldReply,
  type ReplyBriefing,
} from "./replyBriefing.ts";
import { scheduleLearning } from "../practice/scheduling.ts";
import {
  newPracticeSession,
  noteSupport,
  recordAttempt,
  practiceSummary,
} from "../practice/controller.ts";
import { hintSupport } from "../practice/disclosure.ts";
import { taskForPosition, taskAccepts, practiceExample } from "../practice/tasks.ts";
import { loadTrainingContent, branchTeaching } from "../practice/content.ts";
import { practiceBranches, type PracticeBranch } from "../practice/branchCatalog.ts";
import {
  getPracticeStore,
  createMemoryPracticeStore,
  checkpointFromRecord,
  exposureFromRecord,
  type PracticeStore,
  type PracticeCheckpoint,
} from "../practice/store.ts";
import type {
  PracticeSummary,
  PracticeScope,
  PracticeFeedback,
  PracticeTask,
  LearningResult,
  BranchExposure,
} from "../practice/types.ts";
import {
  getReviewStore,
  newMasteryRecord,
  type MasteryRecord,
  type ReviewStore,
} from "../review/store.ts";
import { getAnalytics, type AnalyticsPort } from "../analytics/events.ts";
import { onBook, openingPathForMove, type OpeningPathMove } from "./openingPath.ts";
import { courseReturnPoint } from "./courseRecovery.ts";
import type { CourseSwitchOffer } from "./courseSwitch.ts";
import type { saveHandoff } from "./courseHandoff.ts";

export type TrainMode = "learn" | "recall" | "branches" | "review";

export function parseTrainMode(raw: string | null | undefined): TrainMode {
  if (raw === "recall" || raw === "branches" || raw === "review") return raw;
  return "learn";
}

export type ReviewResult = {
  practiceOnly?: boolean;
  pass: boolean;
  /** The move played, or null when the move was shown without a play. */
  played: string | null;
  assisted: boolean;
  /** The course move for the position. */
  expected: string;
  /** Days until the position comes back. Zero means it returns in this sitting. */
  days: number;
  /** The move played is an authored alternative, not the stored course move. */
  alternative?: boolean;
  /** The course's note on the move played, when it warns against it. */
  note?: string | null;
  /** One sentence of why, shown beside the grade and not part of the spoken sentence. */
  lesson?: string | null;
  repaired?: boolean;
};

/**
 * A Repeat positions move passes only when it is the course move for the
 * position (or a move the course book accepts there, such as a transposition)
 * and is not a tactical error. Any other move, however playable, is a miss.
 */
export function reviewMovePasses(args: {
  san: string;
  expected: string | null | undefined;
  bookStatus: string | null | undefined;
  band: string;
}): boolean {
  if (args.band === "tactical-error") return false;
  const clean = (san: string) => san.replace(/[+#?!]/g, "");
  if (args.expected && clean(args.san) === clean(args.expected)) return true;
  return args.bookStatus === "repertoire" || args.bookStatus === "transposition";
}

/** One sentence on a graded Repeat positions answer, for the board and screen readers. */
export function reviewResultSentence(result: ReviewResult): string {
  const when = result.practiceOnly
    ? "Optional practice keeps the real review date unchanged."
    : result.days <= 0
      ? "It comes back before this sitting ends."
      : result.days <= 1
        ? "It comes back tomorrow."
        : `It comes back in ${result.days} days.`;
  if (!result.pass) {
    const note = result.note ? ` ${result.note}` : "";
    return result.played
      ? `Not this time: you played ${result.played}; the course move is ${result.expected}.${note} ${when}`
      : `Marked to repeat. The course move is ${result.expected}. ${when}`;
  }
  if (!result.played) return `Move not attempted. ${when}`;
  if (result.repaired)
    return `Repaired: ${result.played}. The earlier miss stays in your learning record. ${when}`;
  if (result.alternative)
    return `Right with help: ${result.played} is an acceptable alternative here. The course move is ${result.expected}. ${when}`;
  const bare = (san: string) => san.replace(/[+#?!]/g, "");
  // A move order the course also accepts, but not the stored course move.
  if (bare(result.played) !== bare(result.expected))
    return `${result.assisted ? "Right with help" : "Right"}: ${result.played} reaches a course position by another move order. The course move here is ${result.expected}. ${when}`;
  return result.assisted
    ? `Right with help: ${result.played} is the course move. ${when}`
    : `Right: ${result.played} is the course move. ${when}`;
}

export type HistoryEntry = {
  ply: number;
  /** Identifies this attempt, including when the same move is replayed after undo. */
  turnId?: string;
  byStudent: boolean;
  san: string;
  uci: string;
  fenBefore: string;
  mode?: TrainMode;
  practiceResult?: LearningResult;
  answerRevealed?: boolean;
  qualityBand?: QualityBandId;
  assessment?: MoveAssessment | null;
  /** Authored opening evidence, kept separate from the changing engine score. */
  openingPath?: OpeningPathMove;
  reason?: string;
  isMainline?: boolean;
  recommendedSan?: string | null;
  /** The course's move in this position; null once the course has no move here. */
  courseSan?: string | null;
  /** The course's prepared line had ended by the time this move was made, or ends with it. */
  endsLine?: boolean;
  deepestHint?: HintDepth;
  assisted?: boolean;
  medal?: MedalName | null;
  earnedMedal?: MedalName | null;
  rank?: number | null;
  medalStatus?: MedalStatus;
  withheld?: WithheldReason;
  moveScore?: number | null;
  outsideTop?: boolean;
  /** Legal moves the rank is measured against ("6th / 24"). */
  total?: number | null;
  rankedCount?: number | null;
  percentile?: number | null;
  message?: string;
  setup?: boolean;
  concepts?: Array<{ tag: string; polarity: string }>;
  /** White-POV engine evaluation after this move, in centipawn-comparable units. */
  evalAfterCp?: number | null;
  /** The engine's preferred move in the position before this move, when it differed. */
  engineBestSan?: string | null;
  /** Stockfish's first three choices in this position, from the ranking search. */
  engineChoices?: EngineChoice[];
  /** Generated fragments to replace when authoritative scores change. */
  positionReason?: string | null;
  consequenceReason?: string | null;
};

export type OpponentNote = {
  title: string;
  /** The move number the reply answers, and who played it. */
  moveNumber?: number;
  mover?: "w" | "b";
  /** The structured reply. Absent only on notes saved before this card existed. */
  briefing?: ReplyBriefing;
};

export type CoachCard = {
  san: string;
  moveNumber?: number;
  judgment: QualityBandId;
  reason: string;
  bookStatus: string;
  bookDetail: string;
  engineBand: string;
  tacticalStatus: string;
  planFit: string;
  axesLine: string;
  /** Stockfish's second and third choices, once the ranking search has them. */
  engineChoiceText?: string;
  alternatives: Array<{ san: string; why: string; role?: string; label?: string }>;
  recommendedSan: string | null;
  teaching?: CoachingLayer | null;
  /** Narrative continuity: how this move connects to the course's ideas so far. */
  thread?: string | null;
};

/** Free play continues past the course until this many White moves. */
export const FREE_PLAY_MAX_MOVES = 40;

export type ReportCardChoice = "continue" | "finish" | "later";

/** The course move the student is about to be asked for, with its reasons. */
export type NextBookMove = {
  moveNumber: number;
  san: string;
  /** One sentence: what the move is for. */
  goal: string;
  /** Why it is useful: what it does strategically. */
  why: string;
  /** What it prepares for the moves after it. */
  setsUp: string | null;
  /** The move in words, for players new to notation: "Pawn from c2 to c3". */
  inWords: string | null;
};

export type TrainerSnapshot = {
  sessionId: string;
  slug: string;
  openingId: string;
  title: string;
  playerSide: "w" | "b";
  eco: string;
  limitedCoverage: boolean;
  mode: TrainMode;
  fen: string;
  ply: number;
  moveNumber: number;
  phaseLabel: string;
  objective: string;
  moveOptions: BookAlternative[];
  /** In Main line, the course move to play next, told before the student moves. */
  nextBook: NextBookMove | null;
  /** From memory: a retry that names the course move. Null while the move stays unnamed. */
  recallPrompt: string | null;
  practice: PracticeSummary;
  practiceScope: PracticeScope;
  practiceFeedback: PracticeFeedback | null;
  practiceBranches: PracticeBranch[];
  branchExposure: BranchExposure | null;
  nextReviewAt: number | null;
  practiceSaveError: string | null;
  practiceAlternatives: Array<{ slug: string; title: string; moves: string[] }>;
  practiceRepairRemaining: number;
  /** From memory moves found this sitting while the move stayed unnamed. */
  memoryFound: number;
  /** From memory moves completed this sitting. An undone first miss does not count. */
  memoryAsked: number;
  /** Side lines: the named reply just played, before the student thinks. */
  branchIntro: string | null;
  /** Other named replies the student can switch the opponent's last move to. */
  branchChoices: Array<{ san: string; name: string }>;
  /** Positions that come back within about a day. */
  reviewSoon: number;
  reviewSavedCount: number;
  /** Squares of the opening's hero pieces and of the pieces to keep in reserve now. */
  actors: { hero: string[]; reserve: string[] };
  variationName: string | null;
  sideToMove: "w" | "b";
  engineStatus: string;
  inputLocked: boolean;
  sessionEnded: boolean;
  history: HistoryEntry[];
  coach: CoachCard | null;
  hints: TutorHintStep[];
  hintRevealed: number;
  opponentNote: OpponentNote | null;
  recap: SessionRecap | null;
  liveAnnouncement: string;
  canUndo: boolean;
  legalFrom: Record<string, string[]>;
  reviewDueCount: number;
  reviewRemaining: number;
  /** Positions in this Repeat positions session (fixed at its start). */
  reviewTotal: number;
  /** How the last Repeat positions answer was graded. */
  reviewResult: ReviewResult | null;
  /** The five-move report card waiting to be read, if any. */
  reportCard: ReportCard | null;
  /** Every report card produced this session, oldest first. */
  reportCards: ReportCard[];
  /** Move fifteen is complete and the student has not yet chosen to continue or finish. */
  pendingFinish: boolean;
  /** The student chose to keep playing past the course. */
  freePlay: boolean;
  positionReading: PositionReading | null;
  themes: ThemeStatus[];
  /** The position lies on the course's main line, where its ideas apply. */
  onMainline: boolean;
  courseSwitch: CourseSwitchOffer | null;
  switchBusy: boolean;
  switchError: string | null;
  /** Lessons whose main line passes through the latest off-course position. */
  routeLessons: string[];
  courseJourney: string[];
};

export type EngineLike = {
  mode: string;
  start(): Promise<void>;
  analyze(
    fen: string,
    opts?: { depth?: number; signal?: AbortSignal; multipv?: number }
  ): Promise<{
    bestmove?: string | null;
    info?: { depth?: number; scoreCp?: number; mate?: number | null; pv?: string | string[] };
    candidates?: unknown[];
  }>;
  cancel(reason?: string): void;
};

/** A dedicated MultiPV worker used when the main engine cannot rank every legal move. */
export type RankerLike = {
  rankAll(opts: { fen: string; legalCount: number; depth?: number }): Promise<RankedLine[]>;
  abort(reason?: string): void;
};

export type TrainerDeps = {
  createEngine?: () => EngineLike;
  /** Return null to disable the fallback ranker (tests, or no Worker support). */
  createRanker?: () => RankerLike | null;
  review?: ReviewStore;
  practiceStore?: PracticeStore;
  analytics?: AnalyticsPort;
};

function defaultRanker(): RankerLike | null {
  if (typeof Worker === "undefined") return null;
  return new Ranker() as unknown as RankerLike;
}

function emptySnapshot(): TrainerSnapshot {
  return {
    sessionId: "",
    slug: "",
    openingId: "",
    title: "",
    playerSide: "w",
    eco: "",
    limitedCoverage: false,
    mode: "learn",
    fen: START_FEN,
    ply: 0,
    moveNumber: 0,
    phaseLabel: phaseForPlyCount(1).label,
    objective: phaseForPlyCount(1).goal,
    moveOptions: [],
    nextBook: null,
    recallPrompt: null,
    practice: { independent: 0, cued: 0, repaired: 0, missed: 0, total: 0 },
    practiceScope: {
      prefixSans: [],
      decisions: null,
      branchId: null,
      challenge: false,
      recent: false,
    },
    practiceFeedback: null,
    practiceBranches: [],
    branchExposure: null,
    nextReviewAt: null,
    practiceSaveError: null,
    practiceAlternatives: [],
    practiceRepairRemaining: 0,
    memoryFound: 0,
    memoryAsked: 0,
    branchIntro: null,
    branchChoices: [],
    reviewSoon: 0,
    reviewSavedCount: 0,
    actors: { hero: [], reserve: [] },
    variationName: null,
    sideToMove: "w",
    engineStatus: "Loading engine…",
    inputLocked: true,
    sessionEnded: false,
    history: [],
    coach: null,
    hints: [],
    hintRevealed: 0,
    opponentNote: null,
    recap: null,
    liveAnnouncement: "",
    canUndo: false,
    legalFrom: {},
    reviewDueCount: 0,
    reviewRemaining: 0,
    reviewTotal: 0,
    reviewResult: null,
    reportCard: null,
    reportCards: [],
    pendingFinish: false,
    freePlay: false,
    positionReading: null,
    themes: [],
    onMainline: true,
    courseSwitch: null,
    switchBusy: false,
    switchError: null,
    routeLessons: [],
    courseJourney: [],
  };
}

function engineBandLabel(band: string | undefined): string {
  if (!band || band === "unknown") return "Engine not checked";
  return band;
}

function bookLabel(status: string, variation?: string): { status: string; detail: string } {
  const labels: Record<string, string> = {
    repertoire: "Book repertoire",
    known: "Known sideline",
    warned: "Course warning",
    transposition: "Transposition",
    novel: "Novel",
    unknown: "Unknown",
  };
  const statusLabel = labels[status] || status;
  return {
    status: statusLabel,
    detail: variation ? `${statusLabel} · ${variation}` : statusLabel,
  };
}

function boundaryEnginePhrase(band: MoveAssessment["engine"]["band"] | undefined): string {
  if (band === "top" || band === "equivalent") {
    return "The engine still regards it as fully viable.";
  }
  if (band === "playable") {
    return "The engine regards it as playable, though it changes the course plan.";
  }
  if (band === "inferior") {
    return "The engine sees a concession, so the next move should address the position's most urgent need.";
  }
  return "There is no reliable engine verdict, so the coach will stay with safe opening priorities.";
}

function rankSummaryText(entries: HistoryEntry[]): string | null {
  const summary = summarizeRanks(entries);
  if (summary.averagePercentile == null) return null;
  const medals = [
    summary.gold ? `${summary.gold} gold` : "",
    summary.silver ? `${summary.silver} silver` : "",
    summary.bronze ? `${summary.bronze} bronze` : "",
  ].filter(Boolean);
  return `Top ${summary.averagePercentile}% across ${summary.ranked} ranked ${summary.ranked === 1 ? "move" : "moves"}${medals.length ? ` · ${medals.join(", ")}` : ""}`;
}

function legalFromMap(chess: Chess): Record<string, string[]> {
  const map: Record<string, string[]> = {};
  for (const move of chess.moves({ verbose: true })) {
    if (!map[move.from]) map[move.from] = [];
    map[move.from].push(move.to);
  }
  return map;
}

function tryMove(chess: Chess, spec: string | { from: string; to: string; promotion?: string }) {
  try {
    return chess.move(spec) || null;
  } catch {
    return null;
  }
}

/** The spoken turn prompt: the course move first when the mode teaches it. */
function turnAnnouncement(fields: Partial<TrainerSnapshot>): string {
  const move = fields.nextBook
    ? ` Play ${fields.nextBook.moveNumber}${fields.playerSide === "b" ? "…" : "."} ${fields.nextBook.san}: ${fields.nextBook.goal}`
    : ` ${fields.objective || ""}`;
  return `${fields.playerSide === "b" ? "Black" : "White"} to move.${move}`.trimEnd();
}

export class TrainerSession {
  private listeners = new Set<() => void>();
  private snapshot: TrainerSnapshot = emptySnapshot();
  private machine = new SessionMachine();
  private chess = new Chess();
  private engine: EngineLike | null = null;
  private ranker: RankerLike | null | undefined = undefined;
  private coordinator: AnalysisCoordinator | null = null;
  private course: LessonCourse | null = null;
  private opening: {
    id: string;
    name: string;
    eco: string;
    mainline: string[];
    alternatives?: Record<number, Array<{ san: string; label: string; why: string }>>;
    mainlineWhy?: string[];
  } | null = null;
  private firstDeviationPly: number | null = null;
  private boundaryAnchor: { ply: number; expectedSan: string | null } | null = null;
  /** Off-course teaching on the last move, to rebuild from the position after the reply. */
  private teachingAfterReply: {
    acceptedOption: BookAlternative | undefined;
    title: string;
    moveNumber: number;
    /** The index in the alternative's sample line of the move the card is for. */
    offset: number;
  } | null = null;
  /** The board coaching on the coach card, rebuilt when engine scores arrive. */
  private boardTeaching: {
    acceptedOption: BookAlternative | undefined;
    title: string;
    moveNumber: number;
    replySan: string;
    offset: number;
  } | null = null;
  /** The alternative the student chose and the ply it was played on, while they follow its line. */
  private sampleLine: { option: BookAlternative; startPly: number } | null = null;
  /** Engine scores for every legal move of positions the student has faced. */
  private positionLines = new Map<string, Promise<RankedLine[]>>();
  private positiveConceptCounts: Record<string, number> = {};
  private negativeConceptCounts: Record<string, number> = {};
  private hintUsedThisTurn = false;
  private takeBackUsedThisTurn = false;
  private startRequest = 0;
  private deepestHintThisTurn: HintDepth = 0;
  private prefixSans: string[] = [];
  private createEngine: () => EngineLike;
  private createRanker: () => RankerLike | null;
  private review: ReviewStore;
  private analytics: AnalyticsPort;
  private masteryFlush: Promise<void> = Promise.resolve();
  /**
   * Each position this session saved, as it was before the session first
   * touched it. Saving again (after the next move or a take-back) re-grades
   * from that starting point, so a position is never counted twice.
   */
  private masteryBefore = new Map<string, MasteryRecord | null>();
  /** The grade last recorded for each saved position, for analytics. */
  private masteryGrades = new Map<string, number>();
  /** Bumped when stored progress is reset or imported; older saves then stop. */
  private masteryEpoch = 0;
  private storeGeneration: number | undefined;
  private practiceGeneration: number | undefined;
  /** Moves made before a reset or import: they are not saved again. */
  private masteryIgnored = new Set<string>();
  /**
   * The authored alternative (sample line) the student is following, so a
   * position two lines share is coached from the line actually played.
   */
  private followedLine: string | null = null;
  private learningStore: PracticeStore;
  private learning = newPracticeSession("", "", "learn");
  private evidenceWrites = new Set<Promise<void>>();
  private practiceFlush: Promise<void> = Promise.resolve();
  private pendingPracticeAdvance: (() => Promise<void>) | null = null;
  private pendingReviewAdvance: { nextId: string | null; againId: string | null } | null = null;
  private practiceRepairCount = 0;
  private practiceBaselines = new Map<string, MasteryRecord>();
  private branchExposures = new Map<string, BranchExposure>();
  private reviewQueue: string[] = [];
  /** This Repeat positions session's answers, for its recap. */
  private reviewAnswers: ReviewResult[] = [];
  private reviewCardId: string | null = null;
  /** Positions whose first From memory miss already named the course move. */
  private recallRetries = new Set<string>();
  /** Cards that already returned once in this Repeat positions sitting. */
  private reviewReturns = new Set<string>();
  /** Repeat positions draws from every course, not only the page's opening. */
  private reviewMix = false;
  /** Positions already stored, so a side line's second meeting is recall. */
  private savedKeys = new Set<string>();
  private moveInFlight = false;
  private pendingStudentEntry: HistoryEntry | null = null;
  private reviewBusy = false;
  private dueKeys = new Set<string>();
  private weakKeys = new Set<string>();
  private shownWindows = new Set<number>();
  private reportCardInputs = new Map<number, ReportCardInput>();
  private freePlay = false;
  private pendingFinish = false;
  private offerCourseSwitches = false;
  private resolveSwitch: (() => void) | null = null;
  private declinedSwitchTargets = new Set<string>();
  /** Courses already explained while they still shared a student move. */
  private familyExplained = new Set<string>();
  /** The open dialog was raised from a name click, not from the move in progress. */
  private switchReopened = false;
  private saveHandoffCheckpoint: typeof saveHandoff | null = null;
  private handoffReply: string | null = null;

  constructor(deps: TrainerDeps = {}) {
    this.createEngine = deps.createEngine || (() => new Engine() as EngineLike);
    this.createRanker = deps.createRanker || defaultRanker;
    this.review = deps.review || getReviewStore();
    this.learningStore =
      deps.practiceStore ||
      deps.review?.practiceStore ||
      (deps.review ? createMemoryPracticeStore() : getPracticeStore());
    this.analytics = deps.analytics || getAnalytics();
    // Reset or import on the Progress page replaces stored progress. Saves
    // from the game in progress must not bring the old positions back.
    this.review.onReplace?.(() => this.forgetSavedPositions());
  }

  private forgetSavedPositions(): void {
    this.masteryEpoch += 1;
    this.storeGeneration = undefined;
    void this.review.generation?.().then((value) => {
      this.storeGeneration = value;
    });
    void this.learningStore.generation?.().then((value) => {
      this.practiceGeneration = value;
    });
    this.learning.outcomes = {};
    this.learning.attempts = [];
    this.branchExposures.clear();
    // The store already replaced cards and practice atomically. Do not erase imported sessions.
    this.masteryBefore = new Map();
    this.masteryGrades = new Map();
    this.masteryIgnored = new Set(
      this.snapshot.history.flatMap((entry) => (entry.turnId ? [entry.turnId] : []))
    );
  }

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getSnapshot = (): TrainerSnapshot => this.snapshot;

  /**
   * In a Black course, the note on a White move: its own authored text (a
   * position two lines share keeps only the first line's note), the named
   * line, and Black's next decision.
   */
  /**
   * In a White course, the note on Black's move: the course's own account
   * while the move is in the course, the opening's name the first time it is
   * recognized, and otherwise a description of the move actually played.
   */
  private blackMoveNote(
    fenBefore: string,
    san: string,
    ply: number,
    _history: HistoryEntry[]
  ): OpponentNote | null {
    return this.replyNote(fenBefore, san, ply, "b");
  }

  private whiteMoveNote(fenBefore: string, san: string, ply: number): OpponentNote | null {
    return this.replyNote(fenBefore, san, ply, "w");
  }

  /**
   * The reply card for the move just played: a prepared lesson or a reading
   * of the board. Outside Main line the student recalls the course move, so
   * every sentence that names it is kept back.
   */
  private replyNote(
    fenBefore: string,
    san: string,
    ply: number,
    mover: "w" | "b"
  ): OpponentNote | null {
    const board = new Chess(fenBefore);
    const moveObj = tryMove(board, san);
    if (!moveObj) return null;
    const graph = graphReplyNote(this.course, fenBefore, moveObj.san);
    const base = replyForMove(fenBefore, moveObj, mover, graph);
    const soundness = replySoundness(fenBefore, moveObj.san);
    const line = this.chess.history();
    const bare = (move: string) => move.replace(/[+#?!]/g, "");
    const opening =
      line.length > 0 && bare(line[line.length - 1]!) === bare(moveObj.san)
        ? opponentCategory(line, [base.name, this.course?.title], this.course?.openingId ?? null)
        : "";
    const answer =
      this.snapshot.mode === "learn"
        ? null
        : (teachingMoveFor(this.course?.nodes[positionKey(board.fen())])?.san ?? null);
    const briefing = withheldReply(
      {
        ...base,
        ...(opening ? { opening } : {}),
        ...(soundness ? { soundness } : {}),
      },
      answer
    );
    const moveNumber = mover === "w" ? Math.ceil(ply / 2) : whiteMoveNumber(ply);
    return { title: replyTitle(briefing, moveNumber, mover), moveNumber, mover, briefing };
  }

  private mainlineKeys: { id: string; keys: Set<string> } | null = null;

  /** Whether a position lies on the course's main line, where its ideas apply. */
  private onCourseMainline(fen: string): boolean {
    const id = this.course?.openingId;
    if (!id) return true;
    if (this.mainlineKeys?.id !== id) {
      const sans = isBlackOpening(id)
        ? loadedBlackCourse(id)?.mainline.split(" ") || []
        : [...(MAINLINES[id] || [])];
      const board = new Chess();
      const keys = new Set([positionKey(board.fen())]);
      for (const san of sans) {
        if (!tryMove(board, san)) break;
        keys.add(positionKey(board.fen()));
      }
      this.mainlineKeys = { id, keys };
    }
    return this.mainlineKeys.keys.has(positionKey(fen));
  }

  /**
   * Text without the sentences that name the opponent's move just played:
   * a warning about it has been answered once it is on the board. Black's
   * moves are written "...Nc6"; White's "6.Bf4", or "Bf4" for a piece move
   * (a bare pawn move such as "c4" could be a square).
   */
  private withoutReply(text: string, san: string): string {
    const bare = san.replace(/[+#?!]/g, "").replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const names = new RegExp(
      this.playerSide === "w"
        ? `(\\.\\.\\.|…)${bare}\\b`
        : /^[NBRQKO]/.test(bare)
          ? `(^|[^\\w.…])(\\d+\\.)?${bare}\\b`
          : `\\d+\\.${bare}\\b`
    );
    if (!names.test(text)) return text;
    // A sentence that leans on a dropped one ("c3 does not defend that
    // pawn") would be left dangling, so it goes too.
    const leans = /\b(that|this|those|these|it|its|there|such)\b/i;
    const kept: string[] = [];
    let dropped = false;
    for (const sentence of text.split(/(?<=[.!?])\s+/)) {
      if (names.test(sentence) || (dropped && leans.test(sentence))) {
        dropped = true;
        continue;
      }
      dropped = false;
      kept.push(sentence);
    }
    return kept.join(" ");
  }

  /** The plan for the pawn structure on the board, in one sentence pair. */
  private structureGoal(): string {
    const plan = structurePlanFor(this.course?.openingId, this.chess);
    return `${plan.goal} ${plan.next}`;
  }

  private currentPracticeTask(fen = this.chess.fen()): PracticeTask | null {
    return this.course ? taskForPosition(this.course, fen) : null;
  }

  private practiceCheckpointId(slug = this.snapshot.slug, mode = this.snapshot.mode): string {
    return `session:${slug}:${mode}`;
  }

  private savePractice(): Promise<void> {
    if (this.snapshot.mode === "learn" || !this.learning.id) return this.practiceFlush;
    const checkpoint: PracticeCheckpoint = {
      session: JSON.parse(JSON.stringify(this.learning)),
      route: this.chess.history(),
      feedback: this.snapshot.practiceFeedback,
      ...(this.snapshot.mode === "review"
        ? {
            review: {
              queue: [...this.reviewQueue],
              returns: [...this.reviewReturns],
              answers: [...this.reviewAnswers],
              total: this.snapshot.reviewTotal,
              cardId: this.reviewCardId,
              pending: this.pendingReviewAdvance,
            },
          }
        : {}),
    };
    const id = this.practiceCheckpointId();
    const ended = this.snapshot.sessionEnded;
    const epoch = this.masteryEpoch;
    const generation = this.practiceGeneration;
    this.practiceFlush = this.practiceFlush
      .catch(() => {})
      .then(async () => {
        if (epoch !== this.masteryEpoch) return;
        if (ended) {
          // Keep recent attempt evidence after the resumable cursor is removed.
          await this.learningStore.put(
            {
              id: `completed:${checkpoint.session.id}`,
              kind: "session",
              updatedAt: Date.now(),
              value: checkpoint as unknown as Record<string, unknown>,
            },
            generation
          );
          await this.learningStore.remove(id, generation);
          const past = (await this.learningStore.list())
            .filter((record) => record.kind === "session")
            .sort((a, b) => b.updatedAt - a.updatedAt);
          for (const old of past.slice(100)) await this.learningStore.remove(old.id, generation);
        } else
          await this.learningStore.put(
            {
              id,
              kind: "checkpoint",
              updatedAt: Date.now(),
              value: checkpoint as unknown as Record<string, unknown>,
            },
            generation
          );
      })
      .catch(() => {
        if (epoch !== this.masteryEpoch) return;
        this.patch({
          practiceSaveError:
            "Practice could not be saved on this device. Keep this tab open and try again.",
        });
      });
    return this.practiceFlush;
  }

  private async saveTaskOutcome(task: PracticeTask, result: LearningResult): Promise<void> {
    if (this.learning.scope.recent) return;
    const epoch = this.masteryEpoch;
    const outcomeId = `${this.learning.id}:${task.id}`;
    const baselineRevision = this.practiceBaselines.get(task.id)?.revision || 0;
    const generation = this.storeGeneration;
    const existing = await this.review.get(task.id);
    if (epoch !== this.masteryEpoch || existing?.lastOutcomeId === outcomeId) return;
    if ((existing?.revision || 0) !== baselineRevision) {
      this.patch({
        practiceSaveError:
          "A newer answer from another tab was kept. Start a fresh stretch to update this position.",
      });
      return;
    }
    const record =
      existing ||
      newMasteryRecord({
        courseId: task.courseId,
        openingId: task.openingId,
        positionKey: task.positionKey,
        fen: task.fen,
        recommendedSan: task.expected,
      });
    const next = {
      ...record,
      ...scheduleLearning(record, result),
      recommendedSan: task.expected,
      contentVersion: task.contentVersion,
      lastOutcomeId: outcomeId,
      ...(result === "independent" &&
      (!record.lastRetrievedAt || Date.now() - record.lastRetrievedAt >= 72_000_000)
        ? { lastRetrievedAt: Date.now(), independentReps: (record.independentReps || 0) + 1 }
        : {}),
    };
    if (this.review.putIfCurrent) {
      const saved = await this.review.putIfCurrent(next, record.revision || 0, generation);
      if (!saved)
        this.patch({
          practiceSaveError:
            "This position was updated in another tab. Its newer progress was kept.",
        });
    } else await this.review.put(next);
  }

  /** An optional demonstration records any later answer it actually names. */
  markPracticePreview(
    text = this.snapshot.practiceFeedback?.example || "",
    startFen?: string
  ): void {
    if (this.snapshot.mode === "learn" || !this.course || !text) return;
    const feedbackTask = this.snapshot.practiceFeedback?.taskId;
    const fen =
      startFen ||
      (feedbackTask ? this.learning.outcomes[feedbackTask]?.task.fen : undefined) ||
      [...this.snapshot.history].reverse().find((entry) => entry.byStudent && !entry.setup)
        ?.fenBefore;
    if (!fen) return;
    const board = new Chess(fen);
    for (let i = 0; i < 4; i += 1) {
      const node = this.course.nodes[positionKey(board.fen())];
      const edge = teachingMoveFor(node);
      if (!node || node.handoff || !edge || !board.moves().includes(edge.san)) break;
      const task = i > 0 ? this.currentPracticeTask(board.fen()) : null;
      if (task) {
        const token = task.expected.replace(/[+#!?]/g, "");
        const named = new RegExp(`(^|[^A-Za-z0-9])${token}([^A-Za-z0-9]|$)`).test(text);
        if (named) noteSupport(this.learning, task, "answer");
      }
      board.move(edge.san);
    }
    void this.savePractice();
  }

  private saveRevealedOutcome(task: PracticeTask): void {
    const sessionId = this.machine.sessionId;
    const pending = this.saveTaskOutcome(task, "missed").catch(() => {
      if (this.machine.isCurrent({ sessionId }))
        this.patch({
          practiceSaveError: "The reveal is kept in this tab. Retry saving to keep its progress.",
        });
    });
    this.evidenceWrites.add(pending);
    void pending.finally(() => this.evidenceWrites.delete(pending));
  }

  /**
   * The prompt for a mode that hides the move. From memory always uses the
   * cue. Side lines uses the fuller idea the first time a position is met,
   * and the cue once that position is already stored.
   */
  private hiddenMovePrompt(mode: TrainMode): string | null {
    if (mode !== "recall" && mode !== "branches") return null;
    if (mode === "recall" && this.snapshot.recallPrompt) return this.snapshot.recallPrompt;
    if (this.chess.turn() !== this.playerSide) return null;
    const historyLength = this.chess.history().length;
    const node = this.currentNode();
    const taught = teachingMoveFor(node);
    const book = bookCoachingAtPosition(this.course?.openingId, this.chess.fen(), taught?.san);
    const phase = phaseForPlyCount(Math.max(1, historyLength + 1));
    const fallback =
      this.playerSide === "b" && historyLength <= 8
        ? "Challenge White’s center and develop your pieces toward it."
        : phaseGoalFor(this.course?.openingId, phase.minPly);
    const san = book?.san || taught?.san || null;
    if (!san && !book?.goal && !book?.why && !taught?.explanation) return null;
    const args = {
      goal: book?.goal || (node?.objectiveAuthored ? node.objective : null),
      why: book?.why || taught?.explanation,
      piece: book?.piece,
      san,
      fallback,
    };
    if (
      mode === "branches" &&
      (this.snapshot.branchExposure?.encounters || 0) <= 1 &&
      !this.learning.scope.challenge
    ) {
      const branch = this.snapshot.practiceBranches.find(
        (item) => item.id === this.learning.scope.branchId
      );
      return (
        branchTeaching(this.snapshot.openingId, branch?.name || "")?.idea ||
        this.currentPracticeTask()?.cue ||
        ideaForPosition(args)
      );
    }
    const task = this.currentPracticeTask();
    const repetitions = task ? this.practiceBaselines.get(task.id)?.independentReps || 0 : 0;
    if (repetitions >= 3) return "Find your response. A hint is available whenever you need it.";
    if (repetitions >= 1) return "What is the plan in this position?";
    return task?.cue || memoryCue(args);
  }

  /** Found and asked, from the moves still on the board. */
  private recallCounts(history: HistoryEntry[]): { memoryFound: number; memoryAsked: number } {
    void history;
    const summary = practiceSummary(this.learning);
    return { memoryFound: summary.independent + summary.cued, memoryAsked: summary.total };
  }

  /** One opponent reply this game has not played, named for the recap. */
  private unseenReplySentence(): string | null {
    const course = this.course;
    if (!course) return null;
    const branch = practiceBranches(course).find(
      (item) =>
        !this.branchExposures.has(item.id) &&
        !this.snapshot.history.some(
          (entry) =>
            !entry.byStudent &&
            positionKey(entry.fenBefore) === positionKey(item.before) &&
            entry.san === item.san
        )
    );
    if (branch)
      return `One reply to explore: ${branch.opponent === "b" ? "Black" : "White"}’s ${branch.san}, ${branch.name}.`;
    return null;
  }

  private branchFields(
    fenBefore: string,
    san: string,
    mode: TrainMode = this.snapshot.mode
  ): { branchIntro: string | null; branchChoices: Array<{ san: string; name: string }> } {
    const empty = { branchIntro: null, branchChoices: [] as Array<{ san: string; name: string }> };
    if (mode !== "branches" || !this.course) return empty;
    const replies = namedRepliesAt(this.course, fenBefore);
    const bare = (value: string) => value.replace(/[+#?!]/g, "");
    const played = replies.find((reply) => bare(reply.san) === bare(san));
    if (!played) return empty;
    const opponent = this.playerSide === "w" ? "Black" : "White";
    const intro = `${opponent} played ${played.san} · ${played.name}.`;
    return {
      branchIntro: intro,
      branchChoices:
        replies.length > 1 ? replies.map((reply) => ({ san: reply.san, name: reply.name })) : [],
    };
  }

  /** One sentence of why for a graded Repeat positions answer. */
  private reviewLesson(fen: string): string | null {
    const node = this.course?.nodes[positionKey(fen)];
    const expected = teachingMoveFor(node)?.san;
    const book = bookCoachingAtPosition(this.course?.openingId, fen, expected);
    const name =
      node?.variationName && node.variationName !== this.course?.title ? node.variationName : "";
    const why = (book?.why || teachingMoveFor(node)?.explanation || "").trim();
    const sentence = why.split(/(?<=[.!?])\s+/)[0] || "";
    if (name && sentence) return `${name}. ${sentence}`;
    return name || sentence || null;
  }

  /** A mixed sitting follows the card's course without changing the page slug. */
  private async adoptReviewCourse(card: { openingId: string }): Promise<void> {
    if (!this.reviewMix || card.openingId === this.course?.openingId) return;
    const slug = manifestByOpeningId(card.openingId)?.slug;
    if (!slug) return;
    const sessionId = this.machine.sessionId;
    const course = await loadCourseBySlug(slug);
    if (!course) return;
    await loadTrainingContent(course.openingId);
    if (!this.machine.isCurrent({ sessionId })) return;
    this.course = course;
    this.opening = openingRecord(course.openingId);
    this.mainlineKeys = null;
    this.snapshot = {
      ...this.snapshot,
      openingId: course.openingId,
      title: course.title,
      eco: manifestByOpeningId(course.openingId)?.catalogEco || this.opening?.eco || "",
      practiceBranches: practiceBranches(course),
    };
  }

  /**
   * Put a stored course position on the board by replaying the course moves
   * that lead to it, so move numbers and history are those of a real game.
   * Returns the replayed moves as setup history; falls back to the bare
   * position when the course no longer reaches it.
   */
  private loadCoursePosition(fen: string): HistoryEntry[] {
    const path = this.course ? pathToPosition(this.course, positionKey(fen)) : null;
    const rootFen = this.course?.nodes[this.course.rootPositionKey]?.fen;
    const setup: HistoryEntry[] = [];
    try {
      this.chess.load(rootFen || new Chess().fen());
      for (const san of path || []) {
        const fenBefore = this.chess.fen();
        const moved = tryMove(this.chess, san);
        if (!moved) break;
        setup.push({
          ply: this.chess.history().length,
          byStudent: false,
          san: moved.san,
          uci: moved.from + moved.to + (moved.promotion || ""),
          fenBefore,
          setup: true,
        });
      }
      if (positionKey(this.chess.fen()) === positionKey(fen)) return setup;
      this.chess.load(fen);
    } catch {
      this.chess = new Chess();
    }
    return [];
  }

  /**
   * The plan for the board the session ended on: the course's own middlegame
   * plan when the last move was the course's, otherwise one read from the
   * board. Never a note written for a move that was not played.
   */
  private middlegamePlan(): string | null {
    if (this.playerMovesPlayed(this.chess.history().length) < 15) return null;
    const last = [...this.snapshot.history].reverse().find((entry) => entry.byStudent);
    if (last?.isMainline) {
      const lesson = bookCoachingAtPosition(this.course?.openingId, last.fenBefore, last.san);
      if (lesson?.next) return lesson.next;
    }
    // With the opponent to move, the student's next decision is not yet on
    // the board: the plan is the one for the pawn structure.
    if (this.chess.turn() !== this.playerSide) return this.structureGoal();
    const guide = positionGuideFor(
      this.course?.openingId,
      Math.floor(this.chess.history().length / 2) + 1,
      this.chess
    );
    return `${guide.goal} ${guide.next}`;
  }

  /** Stored positions; unreadable storage counts as none rather than failing the course. */
  private async readCards(which: "due" | "list"): Promise<MasteryRecord[]> {
    try {
      return which === "due" ? await this.review.due() : await this.review.list();
    } catch {
      return [];
    }
  }

  /** Abandon a start still waiting for its course to download. */
  cancelPendingStart(): void {
    this.startRequest += 1;
    if (this.snapshot.switchBusy)
      this.patch({
        switchBusy: false,
        inputLocked: Boolean(this.snapshot.courseSwitch),
        switchError: null,
      });
  }

  /**
   * A game the student can return to: this course, unfinished, at least one
   * move played, and in the requested mode (any mode when none is requested).
   */
  canResume(slug: string, mode?: TrainMode): boolean {
    const snap = this.snapshot;
    return (
      Boolean(this.course) &&
      snap.slug === slug &&
      !snap.sessionEnded &&
      snap.moveNumber > 0 &&
      (mode === undefined || mode === snap.mode)
    );
  }

  private emit() {
    this.listeners.forEach((fn) => fn());
  }

  private get playerSide(): "w" | "b" {
    return this.course?.side === "black" ? "b" : "w";
  }
  private get playerName(): string {
    return this.playerSide === "w" ? "White" : "Black";
  }
  private get opponentName(): string {
    return this.playerSide === "w" ? "Black" : "White";
  }
  private playerMovesPlayed(ply: number): number {
    return this.playerSide === "w" ? whiteMoveNumber(ply) : Math.floor(ply / 2);
  }

  private currentNode(): LessonNode | null {
    if (!this.course) return null;
    return this.course.nodes[positionKey(this.chess.fen())] || null;
  }

  private isCurrent(sessionId: string, turnId: string): boolean {
    return this.machine.isCurrent({ sessionId, turnId });
  }

  private patch(partial: Partial<TrainerSnapshot>) {
    this.snapshot = { ...this.snapshot, ...partial };
    const pending = this.pendingStudentEntry;
    // A legal move is already on the board while engine analysis is pending.
    // Keep it in the reload journal without displaying a premature verdict.
    const checkpoint =
      this.snapshot.mode === "learn" &&
      pending &&
      !this.snapshot.history.some((entry) => entry.turnId === pending.turnId)
        ? { ...this.snapshot, history: [...this.snapshot.history, pending] }
        : this.snapshot;
    this.saveHandoffCheckpoint?.(checkpoint, [...this.shownWindows], [...this.reportCardInputs], {
      hint: this.hintUsedThisTurn,
      takeback: this.takeBackUsedThisTurn,
      depth: this.deepestHintThisTurn,
    });
    this.emit();
  }

  declineCourseSwitch(): void {
    if (this.snapshot.switchBusy) return;
    const offer = this.snapshot.courseSwitch;
    if (offer?.choosable) {
      for (const target of offer.targets) this.declinedSwitchTargets.add(target.slug);
    } else if (offer) {
      // A shared continuation is not a choice, so a later split can still be offered.
      for (const target of offer.targets) this.familyExplained.add(target.slug);
    }
    const reopened = this.switchReopened;
    this.switchReopened = false;
    const resume = this.resolveSwitch;
    this.resolveSwitch = null;
    this.patch({
      courseSwitch: null,
      switchError: null,
      ...(reopened
        ? { inputLocked: false, canUndo: this.snapshot.history.some((entry) => !entry.setup) }
        : {}),
    });
    resume?.();
  }

  /** Open the course explanation again from a taught name in the trail. */
  reopenCourseSwitch(): void {
    // A move already in flight owns the dialog. Reopening it would unlock the
    // board when that move's own prompt is dismissed.
    if (this.moveInFlight || this.snapshot.switchBusy || this.resolveSwitch) return;
    void this.raiseCourseSwitch(true);
  }

  private async raiseCourseSwitch(reopen: boolean): Promise<void> {
    const { courseSwitchOffer } = await import("./courseSwitch.ts");
    const offer = courseSwitchOffer(this.snapshot);
    // Only a real choice opens the dialog; shared lessons stay a note in the trail.
    if (!offer?.choosable || this.snapshot.sessionEnded || this.snapshot.reportCard) return;
    this.switchReopened = reopen;
    this.patch({
      courseSwitch: offer,
      routeLessons: offer.targets.map((target) => target.title),
      inputLocked: true,
      canUndo: false,
      switchError: null,
    });
  }

  /** Change the lesson around the live game; never replay or erase a move. */
  async switchCourse(slug: string, onCommit?: () => void): Promise<boolean> {
    if (
      this.snapshot.switchBusy ||
      this.snapshot.sessionEnded ||
      this.snapshot.reportCard ||
      this.freePlay ||
      !["learn", "branches"].includes(this.snapshot.mode) ||
      (this.snapshot.inputLocked && !this.snapshot.courseSwitch)
    )
      return false;
    const sessionId = this.snapshot.sessionId;
    const fen = this.chess.fen();
    const request = ++this.startRequest;
    const wasLocked = this.snapshot.inputLocked;
    this.patch({ switchBusy: true, switchError: null, inputLocked: true });
    try {
      const [{ courseSwitchOffer }, handoff, course] = await Promise.all([
        import("./courseSwitch.ts"),
        import("./courseHandoff.ts"),
        loadCourseBySlug(slug),
      ]);
      if (
        request !== this.startRequest ||
        sessionId !== this.snapshot.sessionId ||
        fen !== this.chess.fen()
      )
        return false;
      const live = courseSwitchOffer(this.snapshot);
      const target = live?.choosable ? live.targets.find((item) => item.slug === slug) : undefined;
      if (!course || course.side !== this.course?.side || !target)
        throw new Error("This course cannot continue the position on the board.");
      const history = handoff.rebaseHistory(course, this.snapshot.history);
      await this.masteryFlush;
      if (
        request !== this.startRequest ||
        sessionId !== this.snapshot.sessionId ||
        fen !== this.chess.fen()
      )
        return false;
      const journey = this.snapshot.courseJourney.length
        ? this.snapshot.courseJourney
        : [this.snapshot.title];
      this.course = course;
      this.learning.slug = course.slug;
      if (this.snapshot.mode === "branches") this.learning.scope.branchId = `handoff:${course.id}`;
      this.learning.scope.prefixSans = history
        .filter((entry) => entry.setup)
        .map((entry) => entry.san);
      await loadTrainingContent(course.openingId);
      this.declinedSwitchTargets.clear();
      this.familyExplained.clear();
      this.switchReopened = false;
      this.handoffReply = wasLocked ? target.nextMove : null;
      this.opening = openingRecord(course.openingId);
      this.machine.openingId = course.openingId;
      this.boundaryAnchor = null;
      this.firstDeviationPly = null;
      this.followedLine = null;
      this.sampleLine = null;
      this.teachingAfterReply = null;
      this.boardTeaching = null;
      this.masteryBefore = new Map();
      this.masteryGrades = new Map();
      this.saveHandoffCheckpoint = handoff.saveHandoff;
      this.snapshot = {
        ...this.snapshot,
        slug: course.slug,
        openingId: course.openingId,
        title: course.title,
        eco: manifestByOpeningId(course.openingId)?.catalogEco || "",
        limitedCoverage: Boolean(course.limitedCoverage),
        history,
        courseJourney: [...journey, course.title],
        practiceBranches: practiceBranches(course),
        branchExposure: null,
        practiceScope: this.learning.scope,
        coach: null,
        hints: [],
        hintRevealed: 0,
        opponentNote: null,
        courseSwitch: null,
        routeLessons: [],
        switchBusy: false,
        switchError: null,
      };
      const resume = this.resolveSwitch;
      this.resolveSwitch = null;
      // The page follows the course (its URL) in the same render as the new
      // snapshot, so it never shows the old course's page around the new game.
      onCommit?.();
      this.patch({
        ...this.viewFields(),
        inputLocked: wasLocked,
        liveAnnouncement: `Continuing in ${course.title}. Your moves and engine ranks are kept.`,
      });
      try {
        localStorage.setItem("f15m_opening", course.slug);
      } catch {
        /* ignore */
      }
      resume?.();
      if (!wasLocked) this.vetPosition();
      return true;
    } catch {
      if (request === this.startRequest && sessionId === this.snapshot.sessionId)
        this.patch({
          switchBusy: false,
          inputLocked: wasLocked,
          switchError:
            "We couldn’t continue that course. Your game is safe; try again or stay here.",
        });
      return false;
    }
  }

  private async waitForCourseSwitch(sessionId: string, turnId: string): Promise<void> {
    if (
      !this.offerCourseSwitches ||
      this.freePlay ||
      !["learn", "branches"].includes(this.snapshot.mode)
    )
      return;
    const module = await import("./courseSwitch.ts").catch(() => null);
    if (!module) return;
    const { courseSwitchOffer } = module;
    if (!this.isCurrent(sessionId, turnId)) return;
    const offer = courseSwitchOffer(this.snapshot);
    if (!offer) {
      if (this.snapshot.routeLessons.length) this.patch({ routeLessons: [] });
      return;
    }
    const routeLessons = offer.targets.map((target) => target.title);
    const targets = offer.targets.filter((target) => !this.declinedSwitchTargets.has(target.slug));
    // Lessons that still share the student's next move are not a choice: the
    // trail notes them quietly and the game goes on. Only a real choice, not
    // yet declined, pauses the move for the dialog.
    if (!offer.choosable || !targets.length) {
      if (routeLessons.join("|") !== this.snapshot.routeLessons.join("|"))
        this.patch({ routeLessons });
      return;
    }
    const shown = { ...offer, targets };
    // This prompt belongs to the move, not to a trail link opened earlier.
    this.switchReopened = false;
    await new Promise<void>((resolve) => {
      this.resolveSwitch = resolve;
      this.patch({
        courseSwitch: shown,
        routeLessons,
        canUndo: false,
        liveAnnouncement: `${shown.san} reaches another opening we teach. Choose a course before the opponent replies.`,
      });
    });
  }

  private readingCache: {
    fen: string;
    side: "w" | "b";
    vetted: boolean;
    reading: PositionReading;
  } | null = null;
  private themesCache: { key: string; themes: ThemeStatus[] } | null = null;

  /** patch() runs several times per move; the position only changes once. */
  private readingFor(fen: string): PositionReading {
    // The reading is from the student's side, so a White and a Black course
    // reaching the same position (both start from it) read it differently.
    const vetted = hasEngineLines(fen);
    if (
      this.readingCache?.fen !== fen ||
      this.readingCache.side !== this.playerSide ||
      this.readingCache.vetted !== vetted
    )
      this.readingCache = {
        fen,
        side: this.playerSide,
        vetted,
        reading: readPosition(fen, this.playerSide),
      };
    return this.readingCache.reading;
  }

  /**
   * Which history indexes count toward course ideas: every move except the
   * student's moves that were not course moves.
   */
  private themeCounts(extraOffCourse?: number): (index: number) => boolean {
    // Course ideas belong to the course's main line: a move the student
    // played away from the course, or any move in a side line the opponent
    // chose, does not realize one.
    const off = new Set(
      this.snapshot.history
        .filter(
          (entry) =>
            !entry.setup &&
            ((entry.byStudent && !entry.isMainline) || !this.onCourseMainline(entry.fenBefore))
        )
        .map((entry) => entry.ply - 1)
    );
    if (extraOffCourse != null) off.add(extraOffCourse);
    return (index) => !off.has(index);
  }

  private themesFor(fen: string): ThemeStatus[] {
    const offCourse = this.snapshot.history
      .filter((entry) => entry.byStudent && !entry.setup && !entry.isMainline)
      .map((entry) => entry.ply)
      .join(",");
    const key = `${this.course?.openingId}|${this.chess.history().length}|${fen}|${offCourse}`;
    if (this.themesCache?.key !== key) {
      this.themesCache = {
        key,
        themes: themeStatuses(
          this.course?.openingId,
          this.chess.history(),
          fen,
          this.themeCounts()
        ),
      };
    }
    return this.themesCache.themes;
  }

  /**
   * Off-course coaching for the student's last move. `replySan` is the
   * opponent's answer once it has been played, so the forward-looking advice
   * describes the board the student now faces.
   */
  private boundaryTeaching(
    guide: PositionGuide,
    acceptedOption: BookAlternative | undefined,
    title: string,
    moveNumber: number,
    replySan: string | null,
    offset = 0
  ): CoachingLayer {
    // The sample line from the move the card is for.
    const line = acceptedOption?.line?.slice(offset);
    const theirs = (san: string) => `${this.playerSide === "w" ? "…" : ""}${san}`;
    const ours = (san: string) => `${this.playerSide === "b" ? "…" : ""}${san}`;
    // An urgent instruction ("Once it is safe, …") needs the threat it follows.
    const urgent =
      guide.priority === "check" || guide.priority === "material" || guide.priority === "recapture";
    let plan = urgent ? `${guide.goal} ${guide.next}` : guide.next;
    if (line && line.length >= 3) {
      if (replySan === null)
        plan = `If ${this.opponentName} replies ${theirs(line[1])}, you can continue ${ours(line[2])}. ${guide.next}`;
      else if (replySan.replace(/[+#?!]/g, "") === line[1].replace(/[+#?!]/g, ""))
        plan = `${this.opponentName} replied ${theirs(line[1])}, as in the sample line, so ${ours(line[2])} is the natural continuation. ${guide.next}`;
    }
    return {
      kind: "boundary",
      title,
      plan,
      watch: [
        acceptedOption?.watch && replySan
          ? this.withoutReply(acceptedOption.watch, replySan)
          : acceptedOption?.watch,
        guide.watch,
      ]
        .filter(Boolean)
        .join(" "),
      depth:
        acceptedOption && line
          ? {
              strategy: `${acceptedOption.why} One illustrative line is ${formatSampleLine(line, moveNumber, this.playerSide)}. These moves are not forced; compare other replies, especially checks, captures and central breaks.`,
              tradeoff: acceptedOption.watch || guide.watch,
              blackPlan: guide.depth?.blackPlan || "",
              takeaway:
                "Choose an alternative for its plan, then reassess after the opponent’s reply.",
            }
          : guide.depth,
    };
  }

  /**
   * The fields read from the current position. `coach` is the coach card the
   * same update shows, when that update replaces it.
   */
  private viewFields(
    mode: TrainMode = this.snapshot.mode,
    coach: TrainerSnapshot["coach"] = this.snapshot.coach
  ): Partial<TrainerSnapshot> {
    const node = this.currentNode();
    const historyLength = this.chess.history().length;
    const phase = phaseForPlyCount(Math.max(1, historyLength + 1));
    const nextWhiteMove = Math.floor(historyLength / 2) + 1;
    const repertoireMove = teachingMoveFor(node);
    const deepBook =
      mode !== "review" && this.chess.turn() === this.playerSide
        ? bookCoachingAtPosition(this.course?.openingId, this.chess.fen(), repertoireMove?.san)
        : null;
    const continuation =
      !deepBook && mode !== "review" && this.chess.turn() === this.playerSide
        ? reviewedContinuationAtPosition(
            this.course?.openingId,
            this.chess.fen(),
            null,
            this.followedLine
          )
        : null;
    const boundary =
      mode !== "review" &&
      this.chess.turn() === this.playerSide &&
      (!node || !repertoireMove) &&
      historyLength > 0
        ? positionGuideFor(this.course?.openingId, nextWhiteMove, this.chess)
        : null;
    const freePlayGuide =
      this.freePlay &&
      historyLength > MAX_PLIES &&
      this.chess.turn() === this.playerSide &&
      mode !== "review"
        ? positionGuideFor(this.course?.openingId, nextWhiteMove, this.chess)
        : null;
    const hiddenPrompt = this.hiddenMovePrompt(mode);
    // Repeat positions, From memory, and Side lines ask without naming the move.
    // Main line still says it. A recall retry replaces the cue with the move.
    const objective =
      mode === "review"
        ? "Find the course move for this position from memory."
        : hiddenPrompt ||
          (mode === "recall"
            ? this.playerSide === "b" && historyLength <= 8
              ? "Challenge White’s center and develop your pieces toward it."
              : phaseGoalFor(this.course?.openingId, phase.minPly)
            : freePlayGuide?.goal ||
              (historyLength >= MAX_PLIES
                ? this.chess.turn() === this.playerSide
                  ? coach?.teaching?.plan
                  : this.structureGoal()
                : null) ||
              deepBook?.goal ||
              continuation?.note.goal ||
              boundary?.goal ||
              (this.chess.turn() === this.playerSide && repertoireMove
                ? repertoireMove.role === "transposition"
                  ? `${repertoireMove.san} — ${repertoireMove.explanation}`
                  : repertoireMove.explanation
                : null) ||
              authoredObjective(node) ||
              phaseGoalFor(this.course?.openingId, phase.minPly));
    // Only Main line names the course move before it is played.
    const namesMove = mode === "learn";
    const nextCard: Omit<NextBookMove, "inWords"> | null =
      !namesMove || this.chess.turn() !== this.playerSide || historyLength >= MAX_PLIES
        ? null
        : deepBook
          ? {
              moveNumber: nextWhiteMove,
              san: deepBook.san,
              goal: deepBook.goal,
              why: deepBook.why,
              setsUp: deepBook.next || null,
            }
          : continuation
            ? {
                moveNumber: nextWhiteMove,
                san: continuation.note.san,
                goal: continuation.note.goal,
                why: continuation.note.why,
                setsUp: continuation.note.next || null,
              }
            : repertoireMove
              ? {
                  moveNumber: nextWhiteMove,
                  san: repertoireMove.san,
                  goal:
                    authoredObjective(node) || phaseGoalFor(this.course?.openingId, phase.minPly),
                  why: repertoireMove.explanation,
                  setsUp: null,
                }
              : null;
    const fen = this.chess.fen();
    const nextPlayed = nextCard ? tryMove(new Chess(fen), nextCard.san) : null;
    const nextBook: NextBookMove | null = nextCard
      ? { ...nextCard, inWords: nextPlayed ? moveInWords(nextPlayed) : null }
      : null;
    // The coach's recommendation now: never faded as a reserve piece.
    const recommended: string[] = [];
    if (this.chess.turn() === this.playerSide) {
      if (nextPlayed) recommended.push(nextPlayed.from);
      for (const guide of [boundary, freePlayGuide]) {
        if (!guide) continue;
        if (guide.threatened) recommended.push(guide.threatened);
        for (const candidate of guide.candidates) {
          const move = tryMove(new Chess(fen), candidate.san);
          if (move) recommended.push(move.from);
        }
      }
    }
    // Replay the game to follow each hero piece from its starting square.
    const replay = new Chess();
    const trail: Array<{ uci: string; ply: number; fenBefore: string }> = [];
    for (const san of this.chess.history()) {
      const fenBefore = replay.fen();
      const move = tryMove(replay, san);
      if (!move) break;
      trail.push({ uci: `${move.from}${move.to}`, ply: trail.length + 1, fenBefore });
    }
    const hero = heroSquares(this.course?.openingId || "", trail);
    const actors = {
      hero,
      reserve: reserveSquares({
        course: this.course,
        fen,
        side: this.playerSide,
        hero,
        recommended,
      }),
    };
    return {
      fen,
      playerSide: this.playerSide,
      ply: this.chess.history().length,
      moveNumber: this.playerMovesPlayed(this.chess.history().length),
      phaseLabel: phase.label,
      objective,
      moveOptions: namesMove ? deepBook?.options || [] : [],
      nextBook,
      actors,
      variationName: node?.variationName || null,
      sideToMove: this.chess.turn(),
      // Repeat positions grades one position at a time and has no take-back.
      canUndo:
        mode !== "review" &&
        !this.snapshot.sessionEnded &&
        (this.snapshot.history.some((entry) => !entry.setup) ||
          this.chess.history().length > this.prefixSans.length),
      legalFrom: legalFromMap(this.chess),
      positionReading: mode === "review" ? null : this.readingFor(fen),
      themes: mode === "review" ? [] : this.themesFor(fen),
      onMainline: this.onCourseMainline(fen),
      freePlay: this.freePlay,
      pendingFinish: this.pendingFinish,
    };
  }

  async start(opts: {
    slug: string;
    mode?: TrainMode;
    prefixSans?: string[];
    /** Keep a game of this course already in progress instead of starting over. */
    resume?: boolean;
    /** The UI can answer a course choice before the opponent plays. */
    offerCourseSwitches?: boolean;
    /** Repeat positions draws due cards from every course, capped and reordered. */
    mix?: boolean;
    practiceScope?: Partial<PracticeScope>;
  }): Promise<void> {
    // A Black course downloads its content first; if another start() begins
    // meanwhile, the newer request wins.
    const request = ++this.startRequest;
    this.pendingStudentEntry = null;
    if (opts.resume && this.canResume(opts.slug, opts.mode)) {
      this.offerCourseSwitches = opts.offerCourseSwitches ?? this.offerCourseSwitches;
      return;
    }
    // The new mode shows at once, with the course move still hidden, while the
    // sitting restarts. Otherwise Main line could name the move for a moment.
    if (opts.mode) {
      const next = parseTrainMode(opts.mode);
      this.patch({
        mode: next,
        inputLocked: true,
        nextBook: next === "learn" ? this.snapshot.nextBook : null,
        sessionEnded: false,
        recap: null,
      });
    }
    const course = await loadCourseBySlug(opts.slug);
    if (!course || request !== this.startRequest) return;
    // Every game is checkpointed as it is played, so a reload, or a tab
    // Android discarded while another app was in front, picks it up again.
    // Reply lessons load with the game, not with the opening list, and only
    // the packs written for this side and first move.
    const [, handoff] = await Promise.all([
      loadReplyBriefings(
        packsFor(
          isBlackOpening(course.openingId) ? "b" : "w",
          teachingMoveFor(course.nodes[course.rootPositionKey])?.san
        )
      ),
      import("./courseHandoff.ts"),
      loadOpponentCategories(),
    ]);
    const requestedMode = parseTrainMode(opts.mode);
    if (requestedMode !== "learn") await loadTrainingContent(course.openingId);
    await this.practiceFlush;
    await Promise.all([...this.evidenceWrites]);
    let checkpoint =
      opts.resume && requestedMode !== "learn"
        ? checkpointFromRecord(
            await this.learningStore.get(`session:${opts.slug}:${requestedMode}`).catch(() => null)
          )
        : null;
    if (
      checkpoint &&
      (checkpoint.session.slug !== opts.slug ||
        checkpoint.session.mode !== requestedMode ||
        Date.now() - checkpoint.session.updatedAt > 604_800_000 ||
        Object.values(checkpoint.session.outcomes).some(
          (outcome) =>
            outcome.task.openingId === course.openingId &&
            outcome.task.contentVersion !== course.contentVersion
        ))
    )
      checkpoint = null;
    if (request !== this.startRequest) return;
    let saved = opts.resume ? handoff.restoreHandoff(opts.slug, opts.mode) : null;
    let restored: HistoryEntry[] | null = null;
    try {
      if (saved) restored = handoff.rebaseHistory(course, saved.history);
    } catch {
      saved = null;
      restored = null;
    }
    const opening = openingRecord(course.openingId);
    this.engine?.cancel("session-reset");
    this.ranker?.abort("session-reset");
    this.coordinator?.cancelSession(this.machine.sessionId);
    this.course = course;
    this.opening = opening;
    this.chess = new Chess();
    this.firstDeviationPly = null;
    this.boundaryAnchor = null;
    this.positiveConceptCounts = {};
    this.negativeConceptCounts = {};
    this.hintUsedThisTurn = saved?.turnHelp.hint || false;
    this.takeBackUsedThisTurn = saved?.turnHelp.takeback || false;
    this.deepestHintThisTurn = saved?.turnHelp.depth || 0;
    this.prefixSans = restored
      ? restored.filter((entry) => entry.setup).map((entry) => entry.san)
      : opts.prefixSans || checkpoint?.route || [];
    this.pendingPracticeAdvance = null;
    this.pendingReviewAdvance = null;
    this.practiceRepairCount = 0;
    this.reviewQueue = [];
    this.reviewAnswers = [];
    this.reviewCardId = null;
    this.recallRetries = new Set();
    this.reviewReturns = new Set();
    this.reviewMix = Boolean(opts.mix);
    this.savedKeys = new Set();
    // A new session saves against fresh baselines; an earlier session's
    // pending saves keep their own.
    this.masteryBefore = new Map();
    this.masteryGrades = new Map();
    this.masteryIgnored = new Set();
    this.followedLine = null;
    this.boardTeaching = null;
    this.sampleLine = null;
    this.shownWindows = new Set();
    this.reportCardInputs.clear();
    this.freePlay = false;
    this.pendingFinish = saved?.pendingFinish || false;
    this.moveInFlight = false;
    this.reviewBusy = false;
    this.handoffReply = null;
    // A restart from the Practice menu keeps the page's setting.
    this.offerCourseSwitches = opts.offerCourseSwitches ?? this.offerCourseSwitches;
    this.declinedSwitchTargets = new Set();
    this.familyExplained = new Set();
    this.switchReopened = false;
    this.saveHandoffCheckpoint = handoff.saveHandoff;
    if (!saved && opts.mode !== "review") handoff.forgetHandoff();

    this.coordinator = null;
    const snap = this.machine.start(course.openingId);
    this.resolveSwitch?.();
    this.resolveSwitch = null;
    const mode = parseTrainMode(opts.mode || saved?.mode);
    this.learning =
      checkpoint?.session ||
      newPracticeSession(snap.sessionId, opts.slug, mode, {
        prefixSans: opts.prefixSans || [],
        decisions: mode === "recall" ? 5 : mode === "branches" ? 4 : null,
        ...opts.practiceScope,
      });
    if (checkpoint) {
      this.recallRetries = new Set(
        Object.values(this.learning.outcomes)
          .filter((outcome) => outcome.firstCorrect === false)
          .map((outcome) => outcome.task.positionKey)
      );
    }
    const exposureRecords = await this.learningStore.list().catch(() => []);
    if (!this.machine.isCurrent({ sessionId: snap.sessionId })) return;
    this.branchExposures = new Map(
      exposureRecords.flatMap((record) => {
        const exposure = exposureFromRecord(record);
        return exposure ? [[exposure.id, exposure] as const] : [];
      })
    );
    if (mode === "branches" && !opts.prefixSans?.length && !restored && !checkpoint) {
      const available = practiceBranches(course);
      const candidates = available.filter(
        (branch) => branch.decisions >= 3 && branch.prefix.length >= 4
      );
      const branch = [...(candidates.length ? candidates : available)].sort(
        (a, b) =>
          (this.branchExposures.get(a.id)?.encounters || 0) -
            (this.branchExposures.get(b.id)?.encounters || 0) || a.prefix.length - b.prefix.length
      )[0];
      if (branch) {
        this.prefixSans = branch.prefix;
        this.learning.scope.prefixSans = branch.prefix;
        this.learning.scope.branchId = branch.id;
        this.learning.scope.decisions = Math.min(4, branch.decisions);
      }
    }
    this.snapshot = {
      ...emptySnapshot(),
      sessionId: snap.sessionId,
      slug: course.slug,
      openingId: course.openingId,
      title: course.title,
      playerSide: this.playerSide,
      eco: manifestByOpeningId(course.openingId)?.catalogEco || opening?.eco || "",
      limitedCoverage: Boolean(course.limitedCoverage),
      mode,
      practice: practiceSummary(this.learning),
      practiceScope: this.learning.scope,
      practiceRepairRemaining: this.learning.repairTasks.length,
      practiceBranches: practiceBranches(course),
      branchExposure: this.branchExposures.get(this.learning.scope.branchId || "") || null,
      practiceFeedback:
        checkpoint?.feedback && typeof checkpoint.feedback === "object"
          ? (checkpoint.feedback as PracticeFeedback)
          : null,
      engineStatus: "Loading engine…",
      inputLocked: true,
      sessionEnded: false,
      courseJourney: saved?.journey || [],
      reportCards: saved?.reportCards || [],
      reportCard:
        saved?.reportCards.find((card) => card.windowIndex === saved.activeReport) || null,
    };

    // Saves from the game just left must land first, or a position played a
    // moment ago could still look due.
    await this.masteryFlush;
    if (!this.machine.isCurrent({ sessionId: snap.sessionId })) return;

    if (mode === "review") {
      const due = await this.readCards(this.learning.scope.recent ? "list" : "due");
      if (!this.machine.isCurrent({ sessionId: snap.sessionId })) return;
      const mine = opts.mix ? due : due.filter((card) => card.openingId === course.openingId);
      const policies = new Map<string, LessonCourse>();
      await Promise.all(
        [...new Set(mine.map((card) => card.openingId))].map(async (id) => {
          const slug = manifestByOpeningId(id)?.slug;
          const candidate = slug ? await loadCourseBySlug(slug).catch(() => null) : null;
          if (candidate) policies.set(id, candidate);
        })
      );
      if (!this.machine.isCurrent({ sessionId: snap.sessionId })) return;
      const supported = mine.flatMap((card) => {
        const candidate = policies.get(card.openingId);
        let task: PracticeTask | null = null;
        try {
          task = candidate ? taskForPosition(candidate, card.fen) : null;
        } catch {
          /* obsolete question */
        }
        return task
          ? [
              {
                ...card,
                recommendedSan: task.expected,
                taskPolicy: `${task.student}:${task.expected}:${[...task.accepted].sort().join(",")}`,
              },
            ]
          : [];
      });
      const ordered = selectReviewSet(supported);
      this.reviewQueue = ordered.map((card) => card.id);
      for (const card of ordered) {
        await this.adoptReviewCourse(card);
        if (!this.machine.isCurrent({ sessionId: snap.sessionId })) return;
        if (!this.reviewCardIsSupported(card)) {
          this.reviewQueue = this.reviewQueue.filter((id) => id !== card.id);
          continue;
        }
        this.reviewCardId = card.id;
        this.snapshot.history = this.loadCoursePosition(card.fen);
        break;
      }
    } else {
      const setupHistory: HistoryEntry[] = [];
      for (const san of this.prefixSans) {
        const fenBefore = this.chess.fen();
        const moved = tryMove(this.chess, san);
        if (!moved) break;
        setupHistory.push({
          ply: this.chess.history().length,
          byStudent: false,
          san: moved.san,
          uci: moved.from + moved.to + (moved.promotion || ""),
          fenBefore,
          setup: true,
        });
      }
      if (restored) {
        this.chess = new Chess();
        for (const entry of restored) this.chess.move(entry.san);
        this.snapshot.history = restored;
        this.shownWindows = new Set(saved?.shownWindows || []);
        this.reportCardInputs = new Map(saved?.reportInputs || []);
      } else this.snapshot.history = setupHistory;
    }
    // The opponent moves first whenever the board starts with them to move:
    // a Black course's opening White move, or the reply to the last move
    // carried over when the student switches courses mid-game. Either way the
    // reply is setup, chosen by this course's book, not a graded move.
    if (
      mode !== "review" &&
      !this.snapshot.practiceFeedback &&
      this.chess.turn() !== this.playerSide
    ) {
      const san =
        mode === "branches" && !restored
          ? replyFromNamedBranch(course, this.chess.fen())
          : replyFromGraph(course, this.chess.fen());
      const fenBefore = this.chess.fen();
      const moved = san ? tryMove(this.chess, san) : null;
      if (moved) {
        this.snapshot.history.push({
          ply: this.chess.history().length,
          byStudent: false,
          san: moved.san,
          uci: moved.from + moved.to + (moved.promotion || ""),
          fenBefore,
          setup: !restored,
          ...(restored
            ? {
                openingPath: openingPathForMove({
                  course,
                  fen: fenBefore,
                  uci: moved.from + moved.to + (moved.promotion || ""),
                  san: moved.san,
                  byStudent: false,
                }),
              }
            : {}),
        });
        if (!restored) this.prefixSans = this.chess.history();
      }
    }
    this.machine.notePosition(this.chess.fen(), this.chess.history().length);

    const dueAll = await this.readCards("due");
    if (!this.machine.isCurrent({ sessionId: snap.sessionId })) return;
    const allCards = await this.readCards("list");
    if (!this.machine.isCurrent({ sessionId: snap.sessionId })) return;
    this.dueKeys = new Set(dueAll.map((card) => card.positionKey));
    this.weakKeys = new Set(
      allCards.filter((card) => card.box <= 2).map((card) => card.positionKey)
    );
    this.savedKeys = new Set(allCards.map((card) => card.positionKey));
    this.practiceBaselines = new Map(allCards.map((card) => [card.id, card]));
    this.storeGeneration = await this.review.generation?.();
    this.practiceGeneration = await this.learningStore.generation?.();
    if (!this.machine.isCurrent({ sessionId: snap.sessionId })) return;
    const lastSetup = this.snapshot.history.at(-1);
    const branch =
      mode === "branches" && lastSetup && !lastSetup.byStudent
        ? this.branchFields(lastSetup.fenBefore, lastSetup.san, mode)
        : { branchIntro: null, branchChoices: [] as Array<{ san: string; name: string }> };
    const view = this.viewFields();
    this.patch({
      ...view,
      ...branch,
      history: this.snapshot.history,
      coach: null,
      hints: [],
      hintRevealed: 0,
      opponentNote: null,
      recap: null,
      reviewDueCount: dueAll.length,
      reviewRemaining: this.reviewQueue.length,
      reviewTotal: this.reviewQueue.length,
      reviewSavedCount: allCards.filter(
        (card) => this.reviewMix || card.openingId === course.openingId
      ).length,
      reviewSoon: soonCount(allCards, Date.now(), this.reviewMix ? undefined : course.openingId),
      nextReviewAt: allCards
        .filter((card) => this.reviewMix || card.openingId === course.openingId)
        .reduce<number | null>(
          (next, card) =>
            card.due > Date.now() && (next === null || card.due < next) ? card.due : next,
          null
        ),
      practiceScope: this.learning.scope,
      practice: practiceSummary(this.learning),
      liveAnnouncement: [branch.branchIntro, `${this.snapshot.title}. ${turnAnnouncement(view)}`]
        .filter(Boolean)
        .join(" "),
    });

    if (mode === "branches" && this.learning.scope.branchId && !checkpoint)
      await this.recordBranchEncounter(this.learning.scope.branchId);
    if (checkpoint?.review && mode === "review") {
      this.reviewQueue = checkpoint.review.queue;
      this.reviewReturns = new Set(checkpoint.review.returns);
      this.practiceRepairCount = this.reviewReturns.size;
      this.reviewAnswers = checkpoint.review.answers.filter((answer): answer is ReviewResult =>
        Boolean(
          answer &&
          typeof answer === "object" &&
          typeof (answer as ReviewResult).pass === "boolean" &&
          typeof (answer as ReviewResult).expected === "string"
        )
      );
      this.reviewCardId = checkpoint.review.cardId;
      this.pendingReviewAdvance = checkpoint.review.pending;
      const current = this.reviewCardId ? await this.review.get(this.reviewCardId) : null;
      if (current) {
        await this.adoptReviewCourse(current);
        const history = this.loadCoursePosition(current.fen);
        const feedback = checkpoint.feedback;
        if (feedback?.played) {
          const fenBefore = this.chess.fen();
          const moved = tryMove(this.chess, feedback.played);
          if (moved)
            history.push({
              ply: this.chess.history().length,
              byStudent: true,
              fenBefore,
              san: moved.san,
              uci: moved.from + moved.to + (moved.promotion || ""),
              mode: "review",
            });
        }
        this.machine.notePosition(this.chess.fen(), this.chess.history().length);
        this.patch({
          ...this.viewFields(),
          history,
          reviewRemaining: this.reviewQueue.length,
          reviewTotal: checkpoint.review.total,
          practiceFeedback: feedback,
          inputLocked: Boolean(feedback),
        });
      }
    }
    if (checkpoint && mode === "recall") {
      const task = this.currentPracticeTask();
      const outcome = task ? this.learning.outcomes[task.id] : null;
      if (outcome?.answerRevealed && !this.learning.repairing)
        this.patch({
          recallPrompt: `The course move is ${task!.expected}. Play it with its plan in mind.`,
          objective: `The course move is ${task!.expected}. Play it with its plan in mind.`,
        });
    }
    if (this.snapshot.practiceFeedback) {
      const feedback = this.snapshot.practiceFeedback;
      this.patch({
        liveAnnouncement: `${feedback.title}. ${feedback.played ? `You played ${feedback.played}. ` : ""}${feedback.why}`,
      });
    }
    try {
      localStorage.setItem("f15m_opening", course.slug);
      localStorage.setItem("f15m_mode", this.snapshot.mode);
    } catch {
      /* ignore */
    }

    this.analytics.track("course_opened", { slug: course.slug, openingId: course.openingId });
    this.analytics.track("session_start", {
      slug: course.slug,
      openingId: course.openingId,
      mode: this.snapshot.mode,
    });

    if (!this.engine) this.engine = this.createEngine();
    try {
      await this.engine.start();
      if (!this.machine.isCurrent({ sessionId: snap.sessionId })) return;
      // A checkpoint can land between the student's move and an unprepared
      // engine reply. Finish that turn instead of unlocking an opponent board.
      if (restored && !this.snapshot.practiceFeedback && this.chess.turn() !== this.playerSide) {
        await this.computerReply(snap.sessionId, snap.turnId);
        if (!this.machine.isCurrent({ sessionId: snap.sessionId })) return;
        this.patch(this.viewFields());
      }
      this.patch({
        engineStatus: this.engine.mode === "remote" ? "Ready." : "Ready (local engine).",
        inputLocked: Boolean(this.snapshot.reportCard || this.snapshot.practiceFeedback),
      });
      this.vetPosition();
    } catch {
      if (!this.machine.isCurrent({ sessionId: snap.sessionId })) return;
      this.patch({ engineStatus: "Offline — no engine.", inputLocked: true });
      if (restored && !this.snapshot.practiceFeedback && this.chess.turn() !== this.playerSide) {
        await this.computerReply(snap.sessionId, snap.turnId);
        if (!this.machine.isCurrent({ sessionId: snap.sessionId })) return;
      }
      this.patch({
        ...this.viewFields(),
        inputLocked: Boolean(this.snapshot.reportCard || this.snapshot.practiceFeedback),
      });
    }

    for (const entry of this.snapshot.history) {
      if (entry.byStudent && !entry.setup && entry.medalStatus === "pending" && entry.turnId)
        this.trackRanking(
          this.attachMedal({
            sessionId: snap.sessionId,
            turnId: entry.turnId,
            ply: entry.ply,
            uci: entry.uci,
            san: entry.san,
            fenBefore: entry.fenBefore,
            assisted: Boolean(entry.assisted),
            withheld: entry.withheld || null,
          })
        );
    }

    this.coordinator = new AnalysisCoordinator({
      analyze: ((fen, analyzeOpts) => {
        if (!this.engine) return Promise.reject(new Error("engine not started"));
        return this.engine.analyze(fen, analyzeOpts);
      }) as EngineAnalyzeFn,
      timeoutMs: CONFIG.API_TIMEOUT_MS,
      engineVersion: "stockfish",
    });
  }

  setMode(mode: TrainMode): Promise<void> {
    return this.start({
      slug: this.course?.slug || this.snapshot.slug,
      mode,
      mix: mode === "review",
    });
  }

  /** Switch the opponent's last move to another named reply, before the student plays. */
  chooseBranch(san: string): void {
    if (
      this.snapshot.mode !== "branches" ||
      this.snapshot.inputLocked ||
      this.snapshot.sessionEnded
    )
      return;
    if (this.moveInFlight || this.chess.turn() !== this.playerSide) return;
    const last = this.snapshot.history.at(-1);
    if (!last || last.byStudent) return;
    if (!this.snapshot.branchChoices.some((choice) => choice.san === san) || last.san === san)
      return;
    const previous = last.san;
    if (!this.chess.undo()) return;
    const fenBefore = this.chess.fen();
    const moved = tryMove(this.chess, san);
    if (!moved) {
      tryMove(this.chess, previous);
      return;
    }
    const ply = this.chess.history().length;
    const entry: HistoryEntry = {
      ...last,
      ply,
      san: moved.san,
      uci: moved.from + moved.to + (moved.promotion || ""),
      fenBefore,
      openingPath: openingPathForMove({
        course: this.course,
        fen: fenBefore,
        uci: moved.from + moved.to + (moved.promotion || ""),
        san: moved.san,
        byStudent: false,
      }),
    };
    const history = [...this.snapshot.history.slice(0, -1), entry];
    const branch = this.branchFields(fenBefore, moved.san);
    const note =
      this.playerSide === "b"
        ? this.whiteMoveNote(fenBefore, moved.san, ply)
        : this.blackMoveNote(fenBefore, moved.san, ply, history);
    this.machine.notePosition(this.chess.fen(), ply);
    this.patch({
      ...this.viewFields(),
      ...branch,
      history,
      opponentNote: note ?? null,
      liveAnnouncement: branch.branchIntro || `${this.opponentName} played ${moved.san}.`,
    });
  }

  async startBranch(id?: string, challenge = false): Promise<void> {
    if (!this.course || this.moveInFlight || this.reviewBusy) return;
    const branches = practiceBranches(this.course);
    const branch =
      branches.find((item) => item.id === id) ||
      [...branches].sort(
        (a, b) =>
          (this.branchExposures.get(a.id)?.encounters || 0) -
            (this.branchExposures.get(b.id)?.encounters || 0) || a.prefix.length - b.prefix.length
      )[0];
    if (!branch) return;
    await this.start({
      slug: this.course.slug,
      mode: "branches",
      prefixSans: branch.prefix,
      practiceScope: { branchId: branch.id, challenge, decisions: Math.min(4, branch.decisions) },
    });
  }

  async startRecall(decisions = 5, weak = false): Promise<void> {
    if (!this.course) return;
    const studied = this.snapshot.history.filter((entry) => entry.byStudent && !entry.setup);
    const stretchStart = studied[Math.max(0, studied.length - decisions)];
    const studiedPrefix = stretchStart
      ? pathToPosition(this.course, positionKey(stretchStart.fenBefore)) || []
      : this.learning.scope.prefixSans;
    const prefix = weak ? this.repeatWeakPrefix() : decisions === 15 ? [] : studiedPrefix;
    await this.start({
      slug: this.course.slug,
      mode: "recall",
      prefixSans: prefix,
      practiceScope: { decisions, prefixSans: prefix },
    });
  }

  private async recordBranchEncounter(id: string, completed = false): Promise<void> {
    const branch = this.snapshot.practiceBranches.find((item) => item.id === id);
    if (!branch) return;
    const sessionId = this.machine.sessionId;
    const epoch = this.masteryEpoch;
    const saved = await this.learningStore.get(`branch:${id}`).catch(() => null);
    if (!this.machine.isCurrent({ sessionId }) || epoch !== this.masteryEpoch) return;
    const old = (saved ? exposureFromRecord(saved) : null) || this.branchExposures.get(id);
    const now = Date.now();
    const exposure: BranchExposure = {
      id,
      openingId: this.snapshot.openingId,
      name: branch.name,
      firstSeen: old?.firstSeen || now,
      lastSeen: now,
      encounters: (old?.encounters || 0) + (completed ? 0 : 1),
      completed: (old?.completed || 0) + (completed ? 1 : 0),
      challenges: (old?.challenges || 0) + (completed && this.learning.scope.challenge ? 1 : 0),
      preferred: old?.preferred || false,
    };
    this.branchExposures.set(id, exposure);
    this.snapshot = { ...this.snapshot, branchExposure: exposure };
    this.patch({ ...this.viewFields(), branchExposure: exposure });
    try {
      await this.learningStore.put(
        {
          id: `branch:${id}`,
          kind: "exposure",
          updatedAt: now,
          value: exposure as unknown as Record<string, unknown>,
        },
        this.practiceGeneration
      );
    } catch {
      this.patch({ practiceSaveError: "This branch could not be saved on this device." });
    }
  }

  async togglePreferredBranch(): Promise<void> {
    const id = this.learning.scope.branchId;
    const exposure = id ? this.branchExposures.get(id) : null;
    if (!exposure) return;
    const next = { ...exposure, preferred: !exposure.preferred };
    this.branchExposures.set(next.id, next);
    this.patch({ branchExposure: next });
    await this.learningStore
      .put(
        {
          id: `branch:${next.id}`,
          kind: "exposure",
          updatedAt: Date.now(),
          value: next as unknown as Record<string, unknown>,
        },
        this.practiceGeneration
      )
      .catch(() => this.patch({ practiceSaveError: "Your line preference could not be saved." }));
  }

  async retryPracticeSave(): Promise<void> {
    this.patch({ practiceSaveError: null });
    for (const outcome of Object.values(this.learning.outcomes)) {
      if (outcome.firstCorrect !== null)
        await this.saveTaskOutcome(outcome.task, outcome.result).catch(() =>
          this.patch({
            practiceSaveError: "Progress is still unavailable. Your position is kept in this tab.",
          })
        );
    }
    await this.savePractice();
  }

  announce(text: string): void {
    const next = text.trim();
    if (!next) return;
    // Board cursor and selection messages replace each other: a screen reader
    // should hear the square just reached, not the first message of the session.
    this.patch({ liveAnnouncement: next });
  }

  legalDestinations(from: string): string[] {
    return this.chess.moves({ verbose: true, square: from }).map((m) => m.to);
  }

  needsPromotion(from: string, to: string): boolean {
    return this.chess
      .moves({ verbose: true, square: from })
      .some((m) => m.to === to && Boolean(m.promotion));
  }

  async playMove(moveSpec: { from: string; to: string; promotion?: string }): Promise<void> {
    if (this.snapshot.inputLocked || this.snapshot.sessionEnded) return;
    if (this.chess.turn() !== this.playerSide) return;

    const fenBefore = this.chess.fen();
    const moveObj = tryMove(this.chess, moveSpec);
    if (!moveObj) return;

    const task = this.currentPracticeTask(fenBefore);
    if (this.snapshot.mode === "recall" && task && !taskAccepts(task, moveObj.san)) {
      this.chess.undo();
      const outcome = recordAttempt(this.learning, task, moveObj.san, false);
      this.recallRetries.add(task.positionKey);
      this.takeBackUsedThisTurn = true;
      // Lock before storage yields, so rapid taps cannot submit two attempts.
      const sessionId = this.machine.sessionId;
      this.patch({ inputLocked: true });
      try {
        await this.saveTaskOutcome(task, "missed");
      } catch {
        if (this.machine.isCurrent({ sessionId }))
          this.patch({
            practiceSaveError:
              "This attempt is kept in this tab. Retry saving when storage is available.",
          });
      }
      if (!this.machine.isCurrent({ sessionId })) return;
      const revealed = outcome.attempts >= 2;
      if (revealed) {
        noteSupport(this.learning, task, "answer");
        this.hintUsedThisTurn = true;
        this.deepestHintThisTurn = 4;
      }
      const edge = this.currentNode()?.moves.find((candidate) => candidate.san === moveObj.san);
      const differentRoute = edge && edge.role !== "mistake";
      const routeNote = differentRoute
        ? `${moveObj.san} is another opening route. This exercise recalls ${this.snapshot.title}. `
        : "This stretch is asking for its taught response. ";
      const prompt = revealed
        ? `The course move is ${task.expected}. Try it with the plan in mind.`
        : routeNote + task.cue;
      const moves = [...this.chess.history(), moveObj.san];
      const targets = revealed
        ? (await import("./courseSwitch.ts")).switchTargets(this.snapshot.openingId, moves)
        : [];
      if (!this.machine.isCurrent({ sessionId })) return;
      this.patch({
        practiceAlternatives: targets.map((target) => ({
          slug: target.slug,
          title: target.title,
          moves,
        })),
        ...this.viewFields(),
        recallPrompt: prompt,
        objective: prompt,
        practice: practiceSummary(this.learning),
        inputLocked: false,
        practiceFeedback: null,
        liveAnnouncement: `A different route. ${prompt}`,
      });
      await this.savePractice();
      return;
    }

    const sessionSnap = this.machine.beginStudentMove();
    const { sessionId, turnId } = sessionSnap;
    const priorSupport = task ? this.learning.outcomes[task.id]?.support : undefined;
    const withheld: WithheldReason =
      this.hintUsedThisTurn || (priorSupport && priorSupport !== "none")
        ? "hint"
        : this.takeBackUsedThisTurn
          ? "takeback"
          : null;
    const assisted = withheld != null;
    const supportDepth: HintDepth =
      priorSupport === "answer"
        ? 4
        : priorSupport === "candidates"
          ? 3
          : priorSupport === "piece"
            ? 2
            : priorSupport === "goal"
              ? 1
              : 0;
    const deepestHint = Math.max(this.deepestHintThisTurn, supportDepth) as HintDepth;

    this.hintUsedThisTurn = false;
    this.takeBackUsedThisTurn = false;
    this.deepestHintThisTurn = 0;
    this.moveInFlight = true;
    try {
      const ply = this.chess.history().length;
      this.machine.notePosition(this.chess.fen(), ply);
      const modeAtTurn = this.snapshot.mode;
      this.pendingStudentEntry =
        modeAtTurn === "learn"
          ? {
              ply,
              turnId,
              byStudent: true,
              san: moveObj.san,
              uci: moveObj.from + moveObj.to + (moveObj.promotion || ""),
              fenBefore,
              mode: modeAtTurn,
              assisted,
              deepestHint,
              withheld,
              qualityBand: "not-analyzed",
              medalStatus: "unavailable",
            }
          : null;
      this.snapshot = { ...this.snapshot, practiceAlternatives: [] };
      const engineOffline = this.snapshot.engineStatus.startsWith("Offline");
      const engineLoading = this.snapshot.engineStatus.startsWith("Loading");
      const coordinator = this.coordinator;
      const skipEngine = engineOffline || engineLoading || !this.engine || !coordinator;
      this.patch({
        ...this.viewFields(),
        inputLocked: true,
        engineStatus: skipEngine ? this.snapshot.engineStatus : "Thinking…",
        hintRevealed: 0,
        hints: [],
        liveAnnouncement: `${this.playerName} played ${moveObj.san}.`,
      });

      let evalAfter = 0;
      let evalBefore = 0;
      let evalAfterCp: number | null = null;
      let engineBestSan: string | null = null;
      let cpLoss: number | null = null;
      let mateBefore: number | null = null;
      let mateAfter: number | null = null;
      const materialDrop = Math.max(
        0,
        (this.playerSide === "w" ? 1 : -1) *
          (whiteMaterialCp(fenBefore) - whiteMaterialCp(this.chess.fen()))
      );
      if (!skipEngine) {
        // The scores belong to the positions as they stand now; the board
        // may move on while the engine answers.
        const fenAfter = this.chess.fen();
        const turnAfter = this.chess.turn();
        try {
          const [beforeRes, afterRes] = await Promise.all([
            coordinator.analyze({
              fen: fenBefore,
              sessionId,
              turnId,
              depth: CONFIG.ANALYSIS_DEPTH,
            }),
            coordinator.analyze({
              fen: fenAfter,
              sessionId,
              turnId,
              depth: CONFIG.ANALYSIS_DEPTH,
            }),
          ]);
          if (!this.isCurrent(sessionId, turnId)) return;
          const beforeScore = parseEngineScore(beforeRes?.info);
          const afterRaw = parseEngineScore(afterRes?.info);
          const beforeWhite = beforeScore ? toWhitePov(beforeScore, this.playerSide) : null;
          const afterWhite = afterRaw ? toWhitePov(afterRaw, turnAfter) : null;
          if (beforeWhite && afterWhite) {
            cpLoss =
              this.playerSide === "w"
                ? cpLossFromScores(beforeWhite, afterWhite)
                : cpLossFromScores(toWhitePov(beforeWhite, "b"), toWhitePov(afterWhite, "b"));
            evalBefore = scoreToComparable(beforeWhite);
            evalAfter = scoreToComparable(afterWhite);
          }
          if (afterWhite) evalAfterCp = scoreToComparable(afterWhite);
          mateBefore = whitePovMate(beforeWhite && toWhitePov(beforeWhite, this.playerSide));
          mateAfter = whitePovMate(afterWhite && toWhitePov(afterWhite, this.playerSide));
          const bestUci = beforeRes?.bestmove || null;
          if (bestUci && bestUci.length >= 4) {
            const probe = new Chess(fenBefore);
            const best = tryMove(probe, {
              from: bestUci.slice(0, 2),
              to: bestUci.slice(2, 4),
              promotion: bestUci.length > 4 ? bestUci[4] : undefined,
            });
            if (best && best.san !== moveObj.san) engineBestSan = best.san;
            // The engine's own first choice loses nothing, even when two
            // separate searches disagree by a few dozen points.
            else if (best) cpLoss = 0;
          }
        } catch {
          if (!this.isCurrent(sessionId, turnId)) return;
        }
      }
      if (!this.isCurrent(sessionId, turnId)) return;

      const course = this.course;
      let mainlineSan: string | null = null;
      let alternatives: Array<{ san: string; why: string; label?: string; role?: string }> = [];
      let assessment: MoveAssessment | null = null;
      let theory: TheoryResolution | null = null;

      if (course) {
        theory = resolveTheory({
          useGraph: true,
          course,
          fenBefore,
          played: {
            san: moveObj.san,
            uci: moveObj.from + moveObj.to + (moveObj.promotion || ""),
          },
          ply,
          legacyMainline: course.limitedCoverage ? this.opening?.mainline || [] : [],
          legacyAlternatives: course.limitedCoverage ? this.opening?.alternatives : undefined,
        });
        mainlineSan = theory.mainlineSan;
        alternatives = theory.alternatives.map((a) => ({
          san: a.san,
          why: a.why,
          label: a.label,
          role: a.role,
        }));
        assessment = assessMove({
          lookup: theory.lookup,
          inGraph: theory.inGraph,
          pastSlice: theory.pastSlice,
          cpLoss,
          depth: skipEngine ? undefined : CONFIG.ANALYSIS_DEPTH,
          playedSan: moveObj.san,
          mateAfter,
          mateBefore,
          materialDrop,
        });
      }

      const authoredOptions = bookCoachingAtPosition(course?.openingId, fenBefore)?.options || [];
      const acceptedOption = authoredOptions.find((option) => option.san === moveObj.san);
      const reviewed = reviewedContinuationAtPosition(
        course?.openingId,
        fenBefore,
        moveObj.san,
        this.followedLine
      );
      // Any sample move is recognized as book. Before move ten only an
      // authored note takes over the coach card: a generated description of
      // the move says less than "it continues the sample line you chose".
      const reviewedNote =
        reviewed && (reviewed.authored || whiteMoveNumber(ply) >= 10) ? reviewed : null;
      // Every move since the alternative was chosen, this one included,
      // matches its sample line.
      const sample = this.sampleLine?.option;
      const bare = (san: string | undefined) => (san || "").replace(/[+#?!]/g, "");
      const sinceChoice = this.sampleLine
        ? this.chess.history().slice(this.sampleLine.startPly - 1)
        : [];
      const followsSample = Boolean(
        sample?.line &&
        sinceChoice.length >= 3 &&
        sinceChoice.length <= sample.line.length &&
        sinceChoice.every((san, i) => bare(san) === bare(sample.line![i]))
      );
      const sampleOffset = followsSample ? sinceChoice.length - 1 : 0;
      this.sampleLine = acceptedOption
        ? { option: acceptedOption, startPly: ply }
        : followsSample
          ? this.sampleLine
          : null;
      // Playing an alternative (or continuing its sample line) follows that
      // line; any other move leaves it.
      this.followedLine =
        acceptedOption?.name || reviewed?.name || (followsSample ? sample?.name : null) || null;
      alternatives = [
        ...authoredOptions
          .filter((option) => option.san !== moveObj.san)
          .map((option) => ({
            san: option.san,
            why: `${option.name}. ${option.why}`,
            role: "playable",
          })),
        ...alternatives.filter(
          (alternative) => !authoredOptions.some((option) => option.san === alternative.san)
        ),
      ];
      if (reviewed && assessment?.book.status === "novel") {
        assessment.book = {
          status: "known",
          variation: `Reviewed continuation · ${reviewed.name}`,
        };
        assessment.plan = { fit: "advances", explanation: reviewed.note.why };
      }
      if (acceptedOption && assessment) {
        if (assessment.book.status === "novel") {
          assessment.book = { status: "known", variation: acceptedOption.name };
        }
        assessment.plan = {
          fit:
            assessment.book.status === "repertoire" || assessment.book.status === "transposition"
              ? "advances"
              : "neutral",
          explanation: acceptedOption.why,
        };
      }

      const verdict = critique({
        san: moveObj.san,
        uci: moveObj.from + moveObj.to + (moveObj.promotion || ""),
        ply,
        opening: this.opening,
        alternatives,
        mainlineSan,
        cpLoss,
        evalBefore,
        evalAfter,
        side: this.playerSide,
        moveObj,
        historySan: this.chess.history(),
      });
      if (assessment) verdict.assessment = assessment;
      this.machine.assessmentReady();

      const inBook =
        assessment?.book.status === "repertoire" || assessment?.book.status === "transposition";
      if (
        inBook &&
        assessment &&
        assessment.engine.band !== "unknown" &&
        assessment.tactical.status !== "losing"
      ) {
        // A course move is graded as a course move. Its engine band comes from
        // two separate shallow searches whose difference, between near-equal
        // moves, is mostly noise; only an outright tactical loss overrides.
        assessment.engine = { ...assessment.engine, band: "top" };
        if (assessment.tactical.status === "questionable")
          assessment.tactical = { ...assessment.tactical, status: "safe" };
      }
      if (inBook || (cpLoss != null && cpLoss <= 15)) {
        // Authored lines and the engine's own choices explain purposeful
        // retreats and pawn moves themselves. A generic beginner heuristic
        // ("a piece moved twice") must not contradict them.
        verdict.concepts = (verdict.concepts || []).filter(
          (concept) =>
            concept.polarity !== "negative" || !["piece-twice", "flank-pawn"].includes(concept.tag)
        );
      }
      // The course's own move here, if it has one; past the end of a line or
      // in free play there is nothing to leave.
      const courseSan = theory?.inGraph ? theory.lookup?.repertoireMove?.san || null : null;
      if (!inBook && courseSan && this.firstDeviationPly == null) this.firstDeviationPly = ply;
      // Once the course's prepared line has ended (a handoff, the end of a
      // side line, free play), later moves are judged on their own.
      const lineEnded =
        this.freePlay ||
        this.snapshot.history.some((entry) => entry.byStudent && entry.endsLine) ||
        Boolean(theory?.inGraph && !courseSan);
      const nodeBefore = course && theory?.inGraph ? course.nodes[positionKey(fenBefore)] : null;
      // Free play, a handoff already reached, or a position with no prepared
      // move left. A sideline-only position still has prepared moves, so it
      // is not past the book.
      const pastBook =
        this.freePlay ||
        Boolean(this.snapshot.history.at(-1)?.openingPath?.closesBook) ||
        this.snapshot.history.at(-1)?.openingPath?.kind === "beyond" ||
        Boolean(nodeBefore?.handoff) ||
        Boolean(nodeBefore && nodeBefore.moves.length === 0);
      const nodeAfter = course?.nodes[positionKey(this.chess.fen())];
      const endsLine =
        lineEnded ||
        (inBook && Boolean(nodeAfter && (nodeAfter.handoff || !nodeAfter.moves.length)));
      const moveNumber = whiteMoveNumber(ply);
      const deepBook = inBook
        ? bookCoachingAtPosition(course?.openingId, fenBefore, moveObj.san)
        : null;
      const crossedGraphBoundary = Boolean(theory?.inGraph && !theory.lookup?.edge);
      const leftRepertoire = Boolean(!inBook && theory?.inGraph);
      if (leftRepertoire && !this.boundaryAnchor) {
        this.boundaryAnchor = {
          ply,
          expectedSan: theory?.lookup?.repertoireMove?.san || null,
        };
      } else if (inBook && this.boundaryAnchor) {
        this.boundaryAnchor = null;
      }
      const boundaryGuide = !inBook
        ? positionGuideFor(course?.openingId, moveNumber, this.chess)
        : null;
      const expectedSan =
        theory?.lookup?.repertoireMove?.san || this.boundaryAnchor?.expectedSan || null;
      const expectedBook =
        theory?.inGraph && expectedSan
          ? bookCoachingAtPosition(course?.openingId, fenBefore, expectedSan)
          : null;

      const positionReason = boundaryGuide
        ? explainOffBookMove(fenBefore, moveObj, moveNumber <= 2, cpLoss)
        : null;

      const rubric =
        this.playerSide === "w"
          ? computeRubric(verdict, moveObj, ply, this.chess.history(), this.chess.fen())
          : { center: 0, development: 0, "king-safety": 0, tempo: 0 };
      const warned = assessment?.book.status === "warned";
      // The authored note on a known side line or mistake, said as it is.
      const authoredNote = theory?.lookup?.edge ? assessment?.plan.explanation || "" : "";
      const courseMoveNote = courseSan ? `The course move here was ${courseSan}.` : "";
      const boundaryReason = boundaryGuide
        ? [
            theory?.lookup?.edge
              ? [warned ? "The course warns against this move." : "", authoredNote, courseMoveNote]
              : crossedGraphBoundary && expectedSan
                ? moveNumber <= 2
                  ? [`The course move here was ${expectedSan}.`]
                  : [
                      `${moveObj.san} crosses the course boundary here. The book move was ${expectedSan}${expectedBook ? `: ${expectedBook.why}` : "."}`,
                      boundaryEnginePhrase(assessment?.engine.band),
                    ]
                : lineEnded
                  ? [
                      "The course’s prepared moves have ended in this line, so this move is judged on its own.",
                      boundaryEnginePhrase(assessment?.engine.band),
                    ]
                  : followsSample && sample
                    ? [
                        `${moveObj.san} continues the sample line you chose: ${sample.name[0].toLowerCase()}${sample.name.slice(1)}.`,
                        boundaryEnginePhrase(assessment?.engine.band),
                      ]
                    : moveNumber <= 2
                      ? ["This move is outside the authored course line."]
                      : [
                          `${moveObj.san} keeps the game outside the authored line.`,
                          boundaryEnginePhrase(assessment?.engine.band),
                        ],
            // The course's own note says more than the generic reading.
            authoredNote ? null : positionReason,
          ]
            .flat()
            .filter(Boolean)
            .join(" ")
        : null;
      const consequenceReason = acceptedOption ? moveConsequence(fenBefore, moveObj, cpLoss) : null;
      const reason =
        deepBook?.why ||
        reviewedNote?.note.why ||
        (acceptedOption
          ? [
              `${moveObj.san} is an acceptable alternative: ${acceptedOption.name}. ${acceptedOption.why}`,
              consequenceReason,
            ]
              .filter(Boolean)
              .join(" ")
          : null) ||
        boundaryReason ||
        assessment?.plan.explanation ||
        computePlanFit(verdict, moveObj, ply, rubric) ||
        verdict.message;
      const band = qualityBandFromAssessment(assessment);
      const book = bookLabel(
        assessment?.book.status || (verdict.isMainline ? "repertoire" : "novel"),
        assessment?.book.variation
      );

      const recommendedSan = sanitizeRecommendedSan(
        assessment?.recommendedSan ?? verdict.recommended ?? null
      );
      const sansBefore = this.chess.history().slice(0, -1);
      const counts = this.themeCounts(
        inBook && this.onCourseMainline(fenBefore) ? undefined : ply - 1
      );
      const themesBefore = themeStatuses(course?.openingId, sansBefore, fenBefore, counts);
      const themesAfter = themeStatuses(
        course?.openingId,
        this.chess.history(),
        this.chess.fen(),
        counts
      );
      // Only a sound course move realizes a course idea.
      const realized =
        inBook && band !== "tactical-error" ? newlyAchievedThemes(themesBefore, themesAfter) : [];
      const thread = realized.length
        ? `${realized.length === 1 ? "This realizes a course idea" : `This realizes ${realized.length} course ideas`}: ${realized
            .map((theme) => `${theme.title}. ${theme.idea}`)
            .join(
              " "
            )} You have practiced ${themesAfter.filter((t) => t.state === "done").length} of ${themesAfter.length} course ideas.`
        : null;
      // From memory and Side lines test the next move, so the book's notes
      // that name the coming moves stay hidden. The verdict on the move just
      // played remains.
      const recalling = modeAtTurn === "recall" || modeAtTurn === "branches";
      const teaching: CoachingLayer | null = deepBook
        ? {
            kind: "book",
            title: `${moveNumber <= 2 ? "Opening idea" : "Book depth"} · Move ${moveNumber}`,
            plan: recalling ? "" : deepBook.next,
            watch: recalling ? "" : deepBook.watch,
            branches: recalling ? undefined : deepBook.branches,
            depth: recalling ? undefined : deepBook.depth,
          }
        : reviewedNote
          ? {
              kind: "boundary",
              title: `Reviewed continuation · ${reviewedNote.name}`,
              plan: recalling ? "" : reviewedNote.note.next,
              watch: recalling ? "" : reviewedNote.note.watch,
              depth: recalling ? undefined : reviewedNote.note.depth,
            }
          : boundaryGuide
            ? this.boundaryTeaching(
                boundaryGuide,
                acceptedOption ?? (followsSample ? sample : undefined),
                acceptedOption
                  ? `Acceptable alternative · ${acceptedOption.name}`
                  : followsSample && sample
                    ? `Sample line · ${sample.name}`
                    : warned
                      ? "Course warning · coaching from this position"
                      : assessment?.book.status === "known"
                        ? "Known sideline · coaching from this position"
                        : theory?.inGraph && expectedSan
                          ? `Book boundary · ${expectedSan} was the course move`
                          : "Coaching from this position",
                moveNumber,
                null,
                sampleOffset
              )
            : null;
      // The guide above describes the board before the opponent answers; once
      // the reply is on the board, computerReply rebuilds it from there.
      this.teachingAfterReply =
        teaching?.kind === "boundary" && !reviewedNote
          ? {
              acceptedOption: acceptedOption ?? (followsSample ? sample : undefined),
              title: teaching.title,
              moveNumber,
              offset: sampleOffset,
            }
          : null;
      this.boardTeaching = null;

      const learningTask = task;
      const learningOutcome =
        learningTask &&
        (modeAtTurn === "recall" || (modeAtTurn === "branches" && this.learning.scope.challenge))
          ? recordAttempt(
              this.learning,
              learningTask,
              moveObj.san,
              taskAccepts(learningTask, moveObj.san)
            )
          : null;
      const entry: HistoryEntry = {
        ply,
        turnId,
        byStudent: true,
        san: moveObj.san,
        uci: moveObj.from + moveObj.to + (moveObj.promotion || ""),
        fenBefore,
        mode: modeAtTurn,
        practiceResult: learningOutcome?.result,
        answerRevealed: task ? this.learning.outcomes[task.id]?.answerRevealed : undefined,
        qualityBand: band,
        assessment,
        openingPath: openingPathForMove({
          course,
          fen: fenBefore,
          uci: moveObj.from + moveObj.to + (moveObj.promotion || ""),
          san: moveObj.san,
          byStudent: true,
          assessment,
          options: authoredOptions,
          followedVariation:
            acceptedOption?.name || reviewed?.name || (followsSample ? sample?.name : null),
          coaching: reviewed
            ? { why: reviewed.note.why, plan: reviewed.note.next, watch: reviewed.note.watch }
            : followsSample
              ? {
                  why: positionReason || `${moveObj.san} continues the prepared illustration.`,
                  plan: boundaryGuide?.next,
                  watch: boundaryGuide?.watch,
                }
              : undefined,
          beyond: pastBook,
        }),
        reason,
        positionReason: positionReason && reason.includes(positionReason) ? positionReason : null,
        consequenceReason,
        isMainline: inBook,
        recommendedSan,
        courseSan,
        endsLine,
        deepestHint,
        assisted,
        medal: null,
        earnedMedal: null,
        rank: null,
        // Repeat positions grades against the course move and awards no medals.
        medalStatus: skipEngine || modeAtTurn === "review" ? "unavailable" : "pending",
        withheld,
        moveScore: null,
        outsideTop: false,
        message: verdict.message,
        concepts: (verdict.concepts || []).map((c: { tag: string; polarity: string }) => ({
          tag: c.tag,
          polarity: c.polarity,
        })),
        evalAfterCp,
        engineBestSan,
      };

      const coach: CoachCard = {
        san: moveObj.san,
        moveNumber,
        judgment: band,
        reason,
        bookStatus: book.status,
        bookDetail: book.detail,
        engineBand: engineBandLabel(assessment?.engine.band),
        tacticalStatus: assessment?.tactical.status || "unknown",
        planFit: assessment?.plan.fit || "unknown",
        axesLine: assessment ? formatAssessmentAxes(assessment) : "",
        // Other moves only: the played move already has its own verdict.
        // From memory: the other moves' notes can name the coming course
        // moves ("starting the knight tour at once"), so they stay hidden.
        alternatives:
          modeAtTurn === "recall"
            ? []
            : alternatives.filter((alternative) => alternative.san !== moveObj.san),
        recommendedSan,
        teaching,
        thread,
      };

      this.analytics.track("move_played", {
        slug: this.snapshot.slug,
        san: moveObj.san,
        book: assessment?.book.status || null,
        band,
        mode: this.snapshot.mode,
      });
      if (modeAtTurn === "recall" && !this.learning.repairing)
        this.learning.completedDecisions = practiceSummary(this.learning).total;
      else if (modeAtTurn === "branches") this.learning.completedDecisions += 1;
      const playedHistory = [...this.snapshot.history, entry];
      const summary = practiceSummary(this.learning);
      const memory =
        modeAtTurn === "recall"
          ? { memoryFound: summary.independent + summary.cued, memoryAsked: summary.total }
          : null;
      if (modeAtTurn === "recall" && this.snapshot.recallPrompt) {
        this.snapshot = { ...this.snapshot, recallPrompt: null };
      }
      this.patch({
        ...this.viewFields(this.snapshot.mode, coach),
        ...(memory ? { ...memory, recallPrompt: null } : {}),
        practice: summary,
        history: playedHistory,
        coach,
        // The reply card stays until the opponent's next move replaces it.
        opponentNote: this.snapshot.opponentNote,
        liveAnnouncement: `${moveObj.san}. ${book.status}. ${band.replace(/-/g, " ")}.`,
      });
      // Progress is saved as you play, so leaving or restarting keeps it.
      void this.saveMastery();
      if (modeAtTurn === "branches" && learningTask && learningOutcome) {
        await this.saveTaskOutcome(learningTask, learningOutcome.result).catch(() => {
          if (this.isCurrent(sessionId, turnId))
            this.patch({
              practiceSaveError:
                "This challenge is kept in this tab. Retry saving to keep its progress.",
            });
        });
        if (!this.isCurrent(sessionId, turnId)) return;
      }
      // Start ranking while feedback is readable, including the last move of a short stretch.
      if (!skipEngine)
        this.trackRanking(
          this.attachMedal({
            sessionId,
            turnId,
            ply,
            uci: entry.uci,
            san: moveObj.san,
            fenBefore,
            assisted,
            withheld,
          })
        );

      const advance = async () => {
        const whiteMovesPlayed = whiteMoveNumber(ply);
        const courseComplete = !this.freePlay && whiteMovesPlayed >= 15;

        if (modeAtTurn === "review") {
          if (!this.isCurrent(sessionId, turnId)) return;
          // An authored alternative is right, with less credit; a move the
          // course warns against brings its note.
          const accepted = Boolean(
            task && taskAccepts(task, moveObj.san) && band !== "tactical-error"
          );
          // Only an alternative the course coaches on (a sample line from move
          // ten) counts: an option that starts another opening does not.
          const alternative =
            !accepted &&
            Boolean(acceptedOption?.line && acceptedOption.line.length >= 3) &&
            whiteMoveNumber(ply) >= 10 &&
            band !== "tactical-error";
          await this.gradeReview(accepted || alternative, "move", {
            played: moveObj.san,
            assisted: assisted || alternative,
            alternative,
            note: assessment?.book.status === "warned" ? assessment.plan.explanation || null : null,
          });
          return;
        }

        if (this.chess.isGameOver()) {
          this.finishWithFinalReport(whiteMovesPlayed);
          return;
        }

        const scopeLimit = this.learning.scope.decisions;
        const practiceMoves = this.learning.completedDecisions;
        if (modeAtTurn === "recall" && this.learning.repairing) {
          this.learning.repairTasks.shift();
          if (this.learning.repairTasks.length) this.showRecallRepair();
          else this.finishSession("Recall repairs complete.");
          return;
        }
        if (modeAtTurn !== "learn" && scopeLimit && practiceMoves >= scopeLimit) {
          const repairs = Object.values(this.learning.outcomes)
            .filter((outcome) => outcome.firstCorrect === false || outcome.answerRevealed)
            .slice(0, 4);
          if (modeAtTurn === "recall" && repairs.length) {
            this.learning.repairing = true;
            this.learning.repairTasks = repairs.map((outcome) => outcome.task.id);
            this.showRecallRepair();
            return;
          }
          this.finishSession("Practice stretch complete.");
          return;
        }

        if (modeAtTurn !== "learn" && (!this.currentNode() || this.currentNode()?.handoff)) {
          this.finishSession(
            "This authored line ends here. Keep its plan as you continue studying."
          );
          return;
        }

        if (
          !(courseComplete && this.playerSide === "b") &&
          !(this.freePlay && whiteMovesPlayed >= FREE_PLAY_MAX_MOVES && this.playerSide === "b")
        ) {
          if (!courseComplete) await this.waitForCourseSwitch(sessionId, turnId);
          if (!this.isCurrent(sessionId, turnId)) return;
          await this.computerReply(sessionId, turnId);
        }
        if (!this.isCurrent(sessionId, turnId)) return;
        if (this.snapshot.sessionEnded) return;
        if (this.chess.isGameOver()) {
          this.finishWithFinalReport(whiteMovesPlayed);
          return;
        }

        if (this.freePlay && whiteMovesPlayed >= FREE_PLAY_MAX_MOVES) {
          this.finishWithFinalReport(whiteMovesPlayed, "Free play complete.");
          return;
        }

        const windowIndex = whiteMovesPlayed / REPORT_CARD_EVERY;
        if (
          modeAtTurn === "learn" &&
          Number.isInteger(windowIndex) &&
          windowIndex >= 1 &&
          !this.shownWindows.has(windowIndex)
        ) {
          // A window the student mostly did not play (a repeat-weak prefix) has
          // nothing to report; mark it seen and carry on.
          const playedInWindow = this.snapshot.history.filter(
            (h) =>
              h.byStudent &&
              !h.setup &&
              whiteMoveNumber(h.ply) > (windowIndex - 1) * REPORT_CARD_EVERY &&
              whiteMoveNumber(h.ply) <= whiteMovesPlayed
          ).length;
          if (playedInWindow >= 3) {
            this.openReportCard(windowIndex, courseComplete);
            return;
          }
          this.shownWindows.add(windowIndex);
        }

        if (courseComplete || (!this.freePlay && this.chess.history().length >= MAX_PLIES)) {
          this.finishSession();
          return;
        }

        const after = this.viewFields();
        this.patch({
          ...after,
          inputLocked: false,
          engineStatus: this.idleEngineStatus(),
          liveAnnouncement: `${this.snapshot.liveAnnouncement} ${turnAnnouncement(after)}`,
        });
      };
      if (modeAtTurn === "recall" || modeAtTurn === "branches") {
        this.pendingPracticeAdvance = advance;
        this.patch({
          practiceFeedback: {
            taskId: learningTask?.id || positionKey(fenBefore),
            title:
              learningOutcome?.result === "independent"
                ? "Found from memory"
                : learningOutcome?.result === "repaired"
                  ? "Repaired — now practise it again later"
                  : learningOutcome?.result === "cued"
                    ? "Found with support"
                    : "Your plan on the board",
            played: moveObj.san,
            expected: learningTask?.expected || moveObj.san,
            result: learningOutcome?.result || "studied",
            why: coach.reason,
            comparison: this.learning.scope.branchId
              ? branchTeaching(this.course?.openingId || "", this.snapshot.variationName || "")
                  ?.contrast || null
              : null,
            nextLabel: "Continue",
            example: this.course ? practiceExample(this.course, fenBefore) : undefined,
          },
          inputLocked: true,
          canUndo: false,
        });
        await this.savePractice();
        return;
      }
      await advance();
    } finally {
      if (this.isCurrent(sessionId, turnId)) this.moveInFlight = false;
    }
  }

  private async computerReply(sessionId: string, turnId: string): Promise<void> {
    if (!this.isCurrent(sessionId, turnId)) return;
    let replySan: string | null =
      this.handoffReply && this.chess.moves().includes(this.handoffReply)
        ? this.handoffReply
        : null;
    this.handoffReply = null;
    let preparedReply: string | null = null;
    if (this.course && !replySan) {
      const graphSan =
        this.snapshot.mode === "branches" && !this.learning.scope.branchId
          ? replyFromNamedBranch(this.course, this.chess.fen(), {
              dueKeys: this.dueKeys,
              weakKeys: this.weakKeys,
              seenKeys: new Set([
                ...this.snapshot.history.map((entry) => positionKey(entry.fenBefore)),
                positionKey(this.chess.fen()),
              ]),
            })
          : replyFromGraph(this.course, this.chess.fen());
      if (graphSan && this.chess.moves().includes(graphSan)) replySan = graphSan;
    }

    if (!replySan) {
      const reviewedReply = reviewedReplyAtPosition(
        this.course?.openingId,
        this.chess.fen(),
        this.followedLine
      );
      if (reviewedReply && this.chess.moves().includes(reviewedReply)) {
        replySan = reviewedReply;
        preparedReply = reviewedReply;
      }
    }

    const allowPlyReply = Boolean(this.course?.limitedCoverage);
    if (!replySan && allowPlyReply && this.opening) {
      const ply = this.chess.history().length + 1;
      const mainlineSan = this.opening.mainline[ply - 1] || null;
      const studentMoves = this.snapshot.history.filter((h) => h.byStudent);
      const onMainline = studentMoves.every((h, i) => h.san === this.opening?.mainline[i * 2]);
      if (onMainline && mainlineSan && this.chess.moves().includes(mainlineSan)) {
        replySan = mainlineSan;
      }
    }

    let engineSilent = false;
    if (!replySan && this.engine && !this.snapshot.engineStatus.startsWith("Offline")) {
      // A slow or failed search is tried once more, a little shallower.
      for (const depth of [CONFIG.REPLY_DEPTH, Math.max(6, CONFIG.REPLY_DEPTH - 2)]) {
        try {
          const res = await this.engine.analyze(this.chess.fen(), { depth });
          if (!this.isCurrent(sessionId, turnId)) return;
          if (res.bestmove) {
            const from = res.bestmove.slice(0, 2);
            const to = res.bestmove.slice(2, 4);
            const promotion = res.bestmove.length > 4 ? res.bestmove[4] : undefined;
            const preview = this.chess
              .moves({ verbose: true })
              .find(
                (mv) =>
                  mv.from === from && mv.to === to && (!promotion || mv.promotion === promotion)
              );
            if (preview) replySan = preview.san;
          }
          if (replySan) break;
        } catch {
          if (!this.isCurrent(sessionId, turnId)) return;
        }
      }
      // The session goes on without the engine's reply: the board chooses
      // one, so a trap's refutation is still shown and free play continues.
      if (!replySan) {
        replySan = fallbackReply(this.chess);
        engineSilent = Boolean(replySan);
      }
    }

    if (!replySan) {
      const node = this.currentNode();
      const offline = this.snapshot.engineStatus.startsWith("Offline");
      const message = node?.handoff
        ? "The course’s prepared moves end here, and no engine is available to keep playing. The session is complete."
        : offline
          ? `Offline: the course has no ${this.opponentName} reply here. The session is complete.`
          : `The course has no ${this.opponentName} reply here. The session is complete.`;
      this.patch({
        ...this.viewFields(),
        liveAnnouncement: message,
        engineStatus: message,
      });
      this.finishSession(message);
      return;
    }
    if (!this.isCurrent(sessionId, turnId)) return;
    if (this.chess.turn() === this.playerSide) return;
    if (engineSilent)
      this.patch({
        engineStatus: "The engine did not answer in time; the reply was chosen from the board.",
      });

    const fenBefore = this.chess.fen();
    const moveObj = tryMove(this.chess, replySan);
    if (!moveObj) return;
    if (!this.isCurrent(sessionId, turnId)) {
      if (this.chess.fen() !== fenBefore && this.chess.turn() === this.playerSide)
        this.chess.undo();
      return;
    }

    const ply = this.chess.history().length;
    const sample = this.sampleLine;
    const sampleMoves = sample ? this.chess.history().slice(sample.startPly - 1) : [];
    const sampleMatches = Boolean(
      sample?.option.line &&
      sampleMoves.length >= 2 &&
      sampleMoves.length <= sample.option.line.length &&
      sampleMoves.every(
        (san, index) =>
          san.replace(/[+#?!]/g, "") === sample.option.line![index].replace(/[+#?!]/g, "")
      )
    );
    const previousPath = this.snapshot.history.at(-1)?.openingPath;
    const entry: HistoryEntry = {
      ply,
      byStudent: false,
      san: moveObj.san,
      uci: moveObj.from + moveObj.to + (moveObj.promotion || ""),
      fenBefore,
      mode: this.snapshot.mode,
      // The opponent's reply branches the book too: the trail shows the
      // other replies the course prepared for.
      openingPath: openingPathForMove({
        course: this.course,
        fen: fenBefore,
        uci: moveObj.from + moveObj.to + (moveObj.promotion || ""),
        san: moveObj.san,
        byStudent: false,
        followedVariation: sampleMatches
          ? sample?.option.name
          : preparedReply === moveObj.san
            ? "Reviewed continuation"
            : null,
        beyond:
          this.freePlay || Boolean(previousPath?.closesBook) || previousPath?.kind === "beyond",
      }),
    };
    this.machine.opponentReplied(this.chess.fen(), ply);

    const fullHistory = [...this.snapshot.history, entry];
    // A note on the move just played, from the student's side of the board.
    const note =
      this.playerSide === "b"
        ? this.whiteMoveNote(fenBefore, moveObj.san, ply)
        : this.blackMoveNote(fenBefore, moveObj.san, ply, fullHistory);
    const refresh = this.teachingAfterReply;
    this.teachingAfterReply = null;
    this.boardTeaching = refresh ? { ...refresh, replySan: moveObj.san } : null;
    const coach =
      refresh && this.snapshot.coach
        ? {
            ...this.snapshot.coach,
            teaching: this.boundaryTeaching(
              positionGuideFor(
                this.course?.openingId,
                Math.floor(this.chess.history().length / 2) + 1,
                this.chess
              ),
              refresh.acceptedOption,
              refresh.title,
              refresh.moveNumber,
              moveObj.san,
              refresh.offset
            ),
          }
        : this.snapshot.coach;
    // A watch sentence about the reply just played has been answered; the
    // opponent note now describes that move. A sample line's note on what
    // the opponent "answers" becomes what they answered.
    const teaching = coach?.teaching;
    const blackPlan = teaching?.depth?.blackPlan || "";
    const sampleReply = blackPlan.match(
      /^In the sample line, (?:White|Black) answers (?:\.\.\.)?(\S+?)\. /
    );
    const bare = (san: string) => san.replace(/[+#?!]/g, "");
    const shownCoach =
      coach && teaching
        ? {
            ...coach,
            teaching: {
              ...teaching,
              watch:
                teaching.kind === "book"
                  ? this.withoutReply(teaching.watch, moveObj.san)
                  : teaching.watch,
              ...(teaching.depth && sampleReply
                ? {
                    depth: {
                      ...teaching.depth,
                      blackPlan:
                        bare(sampleReply[1]) === bare(moveObj.san)
                          ? `${this.opponentName} answered ${this.playerSide === "w" ? "..." : ""}${moveObj.san}, as in the sample line.`
                          : `${this.opponentName} answered ${this.playerSide === "w" ? "..." : ""}${moveObj.san} instead of the sample line's ${this.playerSide === "w" ? "..." : ""}${sampleReply[1]}. Choose the next move from this position.`,
                    },
                  }
                : {}),
            },
          }
        : coach;
    const branch = this.branchFields(fenBefore, moveObj.san);
    this.patch({
      ...this.viewFields(this.snapshot.mode, shownCoach),
      ...branch,
      coach: shownCoach,
      history: fullHistory,
      opponentNote: note ?? this.snapshot.opponentNote,
      liveAnnouncement: [
        branch.branchIntro || `${this.opponentName} played ${moveObj.san}.`,
        this.latestMedalSentence(),
      ]
        .filter(Boolean)
        .join(" "),
    });
    this.vetPosition();
  }

  /**
   * Score every legal move of the position the student now faces. Static
   * checks cannot see forks, trapped pieces or counterattacks; with the
   * scores in, the coach drops any suggestion or pawn break the engine
   * refutes. The ranking of the student's move here reuses the same search,
   * so scoring the position before the move costs no extra search.
   */
  private vetPosition(): void {
    const fen = this.chess.fen();
    const engine = this.engine;
    if (!engine || this.positionLines.has(fen)) return;
    if (this.snapshot.mode === "review" || this.snapshot.sessionEnded) return;
    if (this.chess.turn() !== this.playerSide || this.chess.isGameOver()) return;
    const sessionId = this.machine.sessionId;
    const legalCount = this.chess.moves().length;
    if (this.positionLines.size > 200) this.positionLines.clear();
    const task = engine
      .analyze(fen, { depth: VET_DEPTH, multipv: Math.min(legalCount, RANK_MAX_LINES) })
      .then((res) => legalLines(fen, linesFromCandidates(res.candidates)))
      .catch(() => [] as RankedLine[]);
    this.positionLines.set(fen, task);
    this.trackRanking(
      task.then((lines) => {
        // A cancelled or failed search can be tried again later.
        if (!lines.length) {
          if (this.positionLines.get(fen) === task) this.positionLines.delete(fen);
          return;
        }
        rememberEngineLines(fen, lines, legalCount);
        if (!this.machine.isCurrent({ sessionId }) || this.chess.fen() !== fen) return;
        this.refreshBoardCoaching();
      })
    );
  }

  /** Rebuild everything read from the board for the current position. */
  private refreshBoardCoaching(): void {
    const teach = this.boardTeaching;
    const coach =
      teach && this.snapshot.coach
        ? {
            ...this.snapshot.coach,
            teaching: this.boundaryTeaching(
              positionGuideFor(
                this.course?.openingId,
                Math.floor(this.chess.history().length / 2) + 1,
                this.chess
              ),
              teach.acceptedOption,
              teach.title,
              teach.moveNumber,
              teach.replySan,
              teach.offset
            ),
          }
        : this.snapshot.coach;
    const node = this.currentNode();
    const hints =
      this.snapshot.hints.length === 5
        ? buildTutorHintLadder({
            ply: this.chess.history().length + 1,
            opening: this.opening,
            chess: this.chess,
            bookSan: teachingMoveFor(node)?.san ?? null,
            candidateSans: (node?.moves || [])
              .filter((m) => m.role !== "mistake" && m.role !== "handoff")
              .map((m) => m.san),
            node,
            course: this.course,
            followedLine: this.followedLine,
          })
        : this.snapshot.hints;
    const view = this.viewFields(this.snapshot.mode, coach);
    // The note on the opponent's last move and the spoken turn prompt read
    // the same board, so they are rebuilt too.
    const history = this.snapshot.history;
    const index = history.map((entry) => !entry.byStudent).lastIndexOf(true);
    const last = index >= 0 ? history[index] : null;
    const note =
      last && !last.setup
        ? this.playerSide === "b"
          ? this.whiteMoveNote(last.fenBefore, last.san, last.ply)
          : this.blackMoveNote(last.fenBefore, last.san, last.ply, history.slice(0, index + 1))
        : null;
    const oldTurn = turnAnnouncement(this.snapshot);
    const newTurn = turnAnnouncement({ ...this.snapshot, ...view });
    const said = this.snapshot.liveAnnouncement;
    this.patch({
      ...view,
      coach,
      hints,
      ...(note ? { opponentNote: note } : {}),
      ...(oldTurn !== newTurn && said.includes(oldTurn)
        ? { liveAnnouncement: said.replace(oldTurn, newTurn) }
        : {}),
      ...this.refreshedReportCard(),
    });
  }

  /** The open report card, rebuilt for the current position's engine scores. */
  private refreshedReportCard(): Partial<TrainerSnapshot> {
    const open = this.snapshot.reportCard;
    const input = open ? this.reportCardInputs.get(open.windowIndex) : undefined;
    if (!open || !input || input.fenEnd !== this.chess.fen()) return {};
    const guide =
      this.chess.turn() === this.playerSide && !this.chess.isGameOver()
        ? positionGuideFor(this.course?.openingId, input.toMove + 1, this.chess)
        : null;
    const refreshedInput: ReportCardInput = {
      ...input,
      guide: guide
        ? { goal: guide.goal, piece: guide.piece, next: guide.next, watch: guide.watch }
        : input.guide,
    };
    this.reportCardInputs.set(open.windowIndex, refreshedInput);
    const card = buildReportCard(refreshedInput);
    return {
      reportCard: card,
      reportCards: this.snapshot.reportCards.map((c) =>
        c.windowIndex === open.windowIndex ? card : c
      ),
    };
  }

  private finishWithFinalReport(whiteMovesPlayed: number, reason?: string): void {
    // A game can end on move 3, 8 or 13. Those windows are real even though
    // they never land on a multiple of five.
    const windowIndex = Math.ceil(whiteMovesPlayed / REPORT_CARD_EVERY);
    const windowStartsAfter = (windowIndex - 1) * REPORT_CARD_EVERY;
    const playedInWindow = this.snapshot.history.filter(
      (entry) =>
        entry.byStudent &&
        !entry.setup &&
        whiteMoveNumber(entry.ply) > windowStartsAfter &&
        whiteMoveNumber(entry.ply) <= whiteMovesPlayed
    ).length;
    if (windowIndex >= 1 && !this.shownWindows.has(windowIndex) && playedInWindow >= 3) {
      this.openReportCard(windowIndex, true);
    }
    const reportCard = this.snapshot.reportCard;
    this.finishSession(reason);
    if (reportCard) this.patch({ reportCard });
  }

  returnToCourse(): void {
    if (this.snapshot.switchBusy || this.snapshot.sessionEnded || this.snapshot.reportCard) return;
    const point = courseReturnPoint(this.snapshot.history);
    if (!point) return;
    // Returning to the decision also lets the player choose that route again.
    this.declinedSwitchTargets.clear();
    this.familyExplained.clear();
    this.switchReopened = false;
    while (this.snapshot.history.length >= point.ply) {
      const length = this.snapshot.history.length;
      this.undo();
      if (this.snapshot.history.length >= length) break;
    }
  }

  undo(): void {
    if (
      this.snapshot.sessionEnded ||
      this.snapshot.mode === "review" ||
      this.snapshot.practiceFeedback
    )
      return;
    const uncommitted = this.chess.history().length > this.snapshot.history.length;
    this.pendingStudentEntry = null;
    if (this.snapshot.history.length === 0 && !uncommitted) return;

    this.engine?.cancel("undo");
    this.ranker?.abort("undo");
    this.coordinator?.cancelSession(this.machine.sessionId);
    this.takeBackUsedThisTurn = true;
    this.hintUsedThisTurn = false;
    this.deepestHintThisTurn = 0;

    let history = this.snapshot.history.slice();
    if (!uncommitted) {
      const last = history[history.length - 1];
      if (last?.setup) return;
      history = history.slice(0, -1);
      if (last && !last.byStudent && history.length > 0 && !history[history.length - 1]?.setup) {
        history = history.slice(0, -1);
      }
    }

    this.rebuildBoard(history);
    if (this.firstDeviationPly != null && this.firstDeviationPly > this.chess.history().length) {
      this.firstDeviationPly = null;
    }
    if (this.boundaryAnchor && this.boundaryAnchor.ply > this.chess.history().length) {
      this.boundaryAnchor = null;
    }
    // A window that has been taken back will be reported again when it is replayed.
    const movesNow = this.playerMovesPlayed(this.chess.history().length);
    for (const windowIndex of [...this.shownWindows]) {
      if (windowIndex * REPORT_CARD_EVERY > movesNow) this.shownWindows.delete(windowIndex);
    }
    this.pendingFinish = false;
    if (movesNow < 15) this.freePlay = false;
    const reportCards = this.snapshot.reportCards.filter((card) => card.toMove <= movesNow);
    this.machine.undo(this.chess.fen(), this.chess.history().length);
    this.handoffReply = null;
    this.resolveSwitch?.();
    this.resolveSwitch = null;
    this.moveInFlight = false;
    for (const windowIndex of this.reportCardInputs.keys()) {
      if (windowIndex * REPORT_CARD_EVERY > movesNow) this.reportCardInputs.delete(windowIndex);
    }
    let opponentNote: TrainerSnapshot["opponentNote"] = null;
    if (this.playerSide === "b") {
      // Describe White's last move still on the board.
      const last = [...history].reverse().find((entry) => !entry.byStudent);
      opponentNote = last ? this.whiteMoveNote(last.fenBefore, last.san, last.ply) : null;
    } else {
      // Describe Black's last move still on the board.
      const index = history.map((entry) => !entry.byStudent).lastIndexOf(true);
      const last = index >= 0 ? history[index] : null;
      opponentNote = last
        ? this.blackMoveNote(last.fenBefore, last.san, last.ply, history.slice(0, index + 1))
        : null;
    }
    const memory = this.snapshot.mode === "recall" ? this.recallCounts(history) : null;
    this.patch({
      ...this.viewFields(),
      ...(memory || {}),
      history,
      canUndo: history.some((entry) => !entry.setup),
      coach: null,
      hints: [],
      hintRevealed: 0,
      recap: null,
      opponentNote,
      reportCard: null,
      reportCards,
      sessionEnded: false,
      inputLocked: false,
      courseSwitch: null,
      routeLessons: [],
      switchBusy: false,
      switchError: null,
      engineStatus: this.idleEngineStatus(),
      liveAnnouncement: `Take back. ${this.playerName} to move.`,
    });
    this.boardTeaching = null;
    this.sampleLine = null;
    this.vetPosition();
    // A taken-back position returns to how it was before this session.
    void this.saveMastery();
  }

  /**
   * Build and show the five-move report card for the window that just ended.
   * Input stays locked until the student acknowledges it; at move fifteen the
   * card also carries the choice between finishing and free play.
   */
  private openReportCard(windowIndex: number, finalWindow: boolean): void {
    const openingId = this.course?.openingId;
    const fromMove = (windowIndex - 1) * REPORT_CARD_EVERY + 1;
    const windowEnd = windowIndex * REPORT_CARD_EVERY;
    const inWindow = (entry: HistoryEntry) => {
      const n = whiteMoveNumber(entry.ply);
      return n >= fromMove && n <= windowEnd;
    };
    const students = this.snapshot.history.filter((h) => h.byStudent && !h.setup && inWindow(h));
    const toMove = students.at(-1) ? whiteMoveNumber(students.at(-1)!.ply) : windowEnd;
    const repliesByPly = new Map(
      this.snapshot.history.filter((h) => !h.byStudent).map((h) => [h.ply, h.san] as const)
    );
    const fenStart = students[0]?.fenBefore || this.chess.fen();
    const fenEnd = this.chess.fen();
    const firstPly = students[0]?.ply || 1;
    const sansBefore = this.chess.history().slice(0, firstPly - 1);
    const moves: ReportCardMove[] = students.map((entry) => {
      const prepared = onBook(entry.openingPath) || (!entry.openingPath && entry.isMainline);
      const positionNote = bookCoachingAtPosition(openingId, entry.fenBefore);
      const lesson = prepared
        ? bookCoachingAtPosition(openingId, entry.fenBefore, entry.san) ||
          reviewedContinuationAtPosition(openingId, entry.fenBefore, entry.san)?.note
        : null;
      const option = prepared ? positionNote?.options?.find((o) => o.san === entry.san) : null;
      const edge = prepared
        ? this.course?.nodes[positionKey(entry.fenBefore)]?.moves.find((m) => m.san === entry.san)
        : null;
      const courseLesson =
        entry.recommendedSan && !entry.isMainline
          ? bookCoachingAtPosition(openingId, entry.fenBefore, entry.recommendedSan)
          : null;
      return {
        moveNumber: whiteMoveNumber(entry.ply),
        san: entry.san,
        fenBefore: entry.fenBefore,
        qualityBand: entry.qualityBand || "not-analyzed",
        medal: entry.medal || null,
        earnedMedal: entry.earnedMedal || null,
        rank: entry.rank ?? null,
        rankTotal: entry.total ?? null,
        percentile: entry.percentile ?? null,
        isMainline: Boolean(entry.isMainline),
        bookStatus: entry.assessment?.book.status || "unknown",
        bookKind: entry.openingPath?.kind,
        assisted: Boolean(entry.assisted),
        deepestHint: entry.deepestHint || 0,
        withheld: entry.withheld || null,
        recommendedSan: entry.recommendedSan || null,
        courseSan: entry.courseSan === undefined ? undefined : entry.courseSan,
        engineBestSan: entry.engineBestSan || null,
        engineChoices: entry.engineChoices,
        cpLoss: entry.assessment?.tactical.cpLoss ?? null,
        evalAfterCp: entry.evalAfterCp ?? null,
        concepts: entry.concepts || [],
        reason: entry.reason || entry.message || "",
        lessonWhy: option?.why || lesson?.why || edge?.explanation || null,
        lessonTakeaway: lesson?.depth?.takeaway || null,
        courseMoveWhy: courseLesson?.why || null,
        variation: entry.openingPath?.name || entry.assessment?.book.variation || null,
      };
    });
    const nextNote =
      this.chess.turn() === this.playerSide ? bookCoachingAtPosition(openingId, fenEnd) : null;
    // Walk the line actually on the board, so a side line gets its own lessons
    // (and fewer of them where it ends), never the main line's by move number.
    const upcoming = nextNote ? this.upcomingLessons(openingId, fenEnd) : [];
    const mainNote = nextNote ? bookCoachingFor(openingId, nextNote.moveNumber) : null;
    const onMainline = Boolean(
      nextNote && mainNote && mainNote.san === nextNote.san && mainNote.goal === nextNote.goal
    );
    const guide =
      this.chess.turn() === this.playerSide && !this.chess.isGameOver()
        ? positionGuideFor(openingId, toMove + 1, this.chess)
        : null;
    const lastStudent = students[students.length - 1];
    const previousStudent = [...this.snapshot.history]
      .reverse()
      .find((h) => h.byStudent && !h.setup && whiteMoveNumber(h.ply) < fromMove);
    const previousStatus = previousStudent?.assessment?.book.status;
    // A recognized alternative is still a prepared road. Only a move that
    // actually left the book makes the next window start off the course.
    const startedOnCourse = previousStudent
      ? previousStudent.openingPath
        ? onBook(previousStudent.openingPath)
        : Boolean(previousStudent.isMainline) ||
          previousStatus === "known" ||
          previousStatus === "transposition"
      : true;
    const middlegamePlan =
      toMove >= 15 && lastStudent?.isMainline ? bookCoachingFor(openingId, 15)?.next || null : null;
    const input: ReportCardInput = {
      openingId: openingId || "",
      openingTitle: this.snapshot.title,
      windowIndex,
      fromMove,
      toMove,
      moves,
      // White's column is the reply that followed. Black's column is the White
      // move being answered: the course can end on Black's move, before White
      // has played, so the two sides deliberately use different ply offsets.
      blackReplies: students.map(
        (h) => repliesByPly.get(h.ply + (this.playerSide === "w" ? 1 : -1)) || ""
      ),
      playerSide: this.playerSide,
      fenStart,
      fenEnd,
      themesBefore: themeStatuses(openingId, sansBefore, fenStart, this.themeCounts()),
      themesAfter: themeStatuses(openingId, this.chess.history(), fenEnd, this.themeCounts()),
      upcoming,
      onMainline,
      nextDecision: nextNote?.goal || null,
      guide: guide
        ? { goal: guide.goal, piece: guide.piece, next: guide.next, watch: guide.watch }
        : null,
      middlegamePlan,
      mode: this.snapshot.mode,
      freePlay: this.freePlay,
      finalWindow,
      engineChecked: moves.some((m) => m.rank != null || m.evalAfterCp != null),
      gameOver: this.chess.isGameOver(),
      gameResult: this.chess.isCheckmate()
        ? this.chess.turn() === this.playerSide
          ? "loss"
          : "win"
        : this.chess.isStalemate()
          ? "stalemate"
          : this.chess.isGameOver()
            ? "draw"
            : null,
      startedOnCourse,
    };
    this.reportCardInputs.set(windowIndex, input);
    const card = buildReportCard(input);
    this.shownWindows.add(windowIndex);
    this.pendingFinish = finalWindow;
    appendReportCardLog({
      ...summarizeReportCard(card, openingId || ""),
      key: `${this.machine.sessionId}:${windowIndex}`,
    });
    this.analytics.track("report_card_shown", {
      slug: this.snapshot.slug,
      window: windowIndex,
      grade: card.grade.word,
      score: card.grade.score,
      mode: this.snapshot.mode,
    });
    this.patch({
      ...this.viewFields(),
      reportCard: card,
      reportCards: [
        ...this.snapshot.reportCards.filter((c) => c.windowIndex !== windowIndex),
        card,
      ],
      pendingFinish: finalWindow,
      inputLocked: true,
      liveAnnouncement: `Report card for moves ${fromMove} to ${toMove}. ${card.grade.word}. ${card.headline}`,
    });
  }

  /**
   * Close the report card. After move fifteen the choice decides whether the
   * session ends with a recap or continues into free play.
   */
  async acknowledgeReportCard(choice?: ReportCardChoice): Promise<void> {
    if (!this.snapshot.reportCard && !this.pendingFinish) return;
    // Without an explicit choice the final card is only closed, never decided.
    choice = choice ?? (this.pendingFinish ? "later" : "continue");
    if (this.snapshot.sessionEnded) {
      this.patch({ reportCard: null });
      return;
    }
    if (this.pendingFinish) {
      if (choice === "finish") {
        this.pendingFinish = false;
        this.patch({ reportCard: null, pendingFinish: false });
        this.finishSession();
        return;
      }
      if (choice === "later") {
        this.patch({ reportCard: null, pendingFinish: true, inputLocked: true });
        return;
      }
      this.pendingFinish = false;
      this.freePlay = true;
      this.machine.continueFreePlay();
      this.analytics.track("free_play_started", {
        slug: this.snapshot.slug,
        mode: this.snapshot.mode,
      });
      if (this.chess.turn() !== this.playerSide && !this.chess.isGameOver()) {
        const { sessionId, turnId } = this.machine.snapshot;
        this.patch({ reportCard: null, pendingFinish: false, inputLocked: true });
        await this.computerReply(sessionId, turnId);
        if (!this.isCurrent(sessionId, turnId) || this.snapshot.sessionEnded) return;
        // White's reply can end the game (mate, stalemate or a draw).
        if (this.chess.isGameOver()) {
          this.finishSession();
          return;
        }
      }
      const after = this.viewFields();
      this.patch({
        ...after,
        reportCard: null,
        pendingFinish: false,
        freePlay: true,
        inputLocked: false,
        engineStatus: this.idleEngineStatus(),
        liveAnnouncement: `Free play. The course is complete; coaching continues from the board. ${turnAnnouncement(after)}`,
      });
      return;
    }
    const after = this.viewFields();
    this.patch({
      ...after,
      reportCard: null,
      inputLocked: false,
      engineStatus: this.idleEngineStatus(),
      liveAnnouncement: turnAnnouncement(after),
    });
  }

  revealHint(): void {
    if (this.snapshot.inputLocked || this.chess.turn() !== this.playerSide) return;
    if (this.snapshot.hintRevealed >= 5) return;
    const node = this.currentNode();
    const bookSan = teachingMoveFor(node)?.san ?? null;
    const candidateSans = (node?.moves || [])
      .filter((m) => m.role !== "mistake" && m.role !== "handoff")
      .map((m) => m.san);
    const hints =
      this.snapshot.hints.length === 5
        ? this.snapshot.hints
        : buildTutorHintLadder({
            ply: this.chess.history().length + 1,
            opening: this.opening,
            chess: this.chess,
            bookSan,
            candidateSans,
            node,
            course: this.course,
            followedLine: this.followedLine,
            hideSolution: this.snapshot.mode !== "learn",
            retrievalCue: this.currentPracticeTask()?.cue,
          });
    const next = Math.min(5, this.snapshot.hintRevealed + 1) as HintDepth;
    this.markPracticePreview(hints[next - 1]?.text || "", this.chess.fen());
    const task = this.currentPracticeTask();
    if (task && this.snapshot.mode !== "learn") {
      const support = hintSupport(hints[next - 1]?.id || "goal", hints[next - 1]?.revealsAnswer);
      noteSupport(this.learning, task, support);
      if (
        support === "answer" &&
        (this.snapshot.mode === "recall" ||
          this.snapshot.mode === "review" ||
          this.learning.scope.challenge)
      ) {
        const outcome = this.learning.outcomes[task.id];
        if (outcome.firstCorrect === null)
          recordAttempt(this.learning, task, null, false, "answer");
        this.saveRevealedOutcome(task);
        this.patch({ practice: practiceSummary(this.learning) });
      }
      void this.savePractice();
    }
    this.hintUsedThisTurn = true;
    this.deepestHintThisTurn = Math.max(this.deepestHintThisTurn, next) as HintDepth;
    this.analytics.track("hint_used", {
      slug: this.snapshot.slug,
      depth: next,
      mode: this.snapshot.mode,
    });
    const medalNote =
      this.snapshot.hintRevealed === 0
        ? this.snapshot.mode === "recall"
          ? " Goal cue used; the move stays hidden."
          : " This move will not earn a medal."
        : "";
    this.patch({
      hints,
      hintRevealed: next,
      liveAnnouncement: `Hint ${next} of 5. ${hints[next - 1]?.label || "Hint"}.${medalNote}`,
    });
  }

  /** Rebuild verdict-dependent recap fields when a delayed ranking arrives. */
  private buildRecap(history: HistoryEntry[], reportCards: ReportCard[]): SessionRecap {
    this.positiveConceptCounts = {};
    this.negativeConceptCounts = {};
    for (const entry of history) {
      if (!entry.byStudent) continue;
      for (const concept of entry.concepts || []) {
        // A course move is never a bad habit, whatever the legacy tags say.
        if (concept.polarity === "negative" && entry.isMainline) continue;
        const bucket =
          concept.polarity === "positive" ? this.positiveConceptCounts : this.negativeConceptCounts;
        bucket[concept.tag] = (bucket[concept.tag] || 0) + 1;
      }
    }
    const studentMoves: RecapStudentMove[] = history
      .filter((h) => h.byStudent)
      .map((h) => ({
        ply: h.ply,
        san: h.san,
        fenBefore: h.fenBefore,
        qualityBand: h.qualityBand || "not-analyzed",
        assisted: Boolean(h.assisted),
        deepestHint: h.deepestHint || 0,
        isMainline: Boolean(h.isMainline),
        recommendedSan: h.recommendedSan,
        courseSan: h.courseSan,
        message: h.message,
      }));
    const themes = this.snapshot.themes.length
      ? this.snapshot.themes
      : themeStatuses(
          this.course?.openingId,
          this.chess.history(),
          this.chess.fen(),
          this.themeCounts()
        );
    // Repeat positions answers one position at a time: its recap counts the
    // answers, not the moves of a line.
    const recap =
      this.snapshot.mode === "review"
        ? buildReviewRecap(this.reviewAnswers)
        : buildSessionRecap({
            studentMoves,
            opening: this.opening,
            positiveCounts: this.positiveConceptCounts,
            negativeCounts: this.negativeConceptCounts,
            firstDeviationPly: this.firstDeviationPly,
            reportCards: reportCards.map((card) => ({
              title: card.title,
              word: card.grade.word,
              score: card.grade.score,
            })),
            themes: {
              done: themes.filter((t) => t.state === "done").length,
              total: themes.length,
              achieved: themes.filter((t) => t.state === "done").map((t) => t.title),
              missed: themes.filter((t) => t.state === "missed").map((t) => t.title),
            },
            freePlayMoves: Math.max(0, studentMoves.length - 15),
            movesPlayed: studentMoves.length,
            rankSummary: rankSummaryText(history.filter((h) => h.byStudent && !h.setup)),
          });
    if (this.snapshot.mode === "review" && this.learning.scope.recent) {
      recap.headline = "Optional practice complete.";
      recap.oneRule =
        "This practice kept your scheduled review dates unchanged. Return to due positions when you are ready.";
    }
    if (this.snapshot.mode !== "review") recap.middlegamePlan = this.middlegamePlan();
    if (this.snapshot.mode === "recall") {
      const summary = practiceSummary(this.learning);
      recap.practice = summary;
      recap.movesPlayed = summary.total;
      recap.independentCount = summary.independent;
      recap.assistedCount = summary.cued + summary.repaired;
      recap.independentLabel = `${summary.independent} found alone · ${summary.cued} with a cue · ${summary.repaired} repaired · ${summary.missed} to revisit`;
      recap.headline = summary.missed
        ? "A useful start. Keep the difficult decisions close."
        : summary.repaired
          ? "The line is stronger after those repairs."
          : summary.cued
            ? "Good recall, with a little support."
            : "Found from memory.";
      const weak = Object.values(this.learning.outcomes).find(
        (outcome) => outcome.firstCorrect === false || outcome.support !== "none"
      );
      recap.onePosition = null;
      if (weak)
        recap.onePosition = {
          moveNumber: Number(weak.task.fen.split(" ")[5]) || 1,
          san: weak.task.expected,
          fenBefore: weak.task.fen,
          note: weak.task.cue,
        };
      recap.oneRule = [
        recap.independentLabel + ".",
        this.unseenReplySentence() || "Choose another short stretch when you are ready.",
      ].join(" ");
    }
    if (this.snapshot.mode === "branches" && !this.learning.scope.challenge) {
      const branch = this.snapshot.practiceBranches.find(
        (item) => item.id === this.learning.scope.branchId
      );
      recap.headline = branch ? `You explored ${branch.name}.` : "A new plan explored.";
      recap.independentCount = 0;
      recap.assistedCount = 0;
      recap.independentLabel = "Branch study · retrieval has not been tested";
      recap.oneRule =
        branchTeaching(this.snapshot.openingId, branch?.name || "")?.contrast ||
        "Recognize the opponent’s changed plan, then adapt your response. Try this reply again with less help.";
    } else if (this.snapshot.mode === "learn") {
      recap.independentLabel = `${studentMoves.length} moves studied · try a short stretch from memory next`;
    }
    return recap;
  }

  finishSession(reason?: string): void {
    if (this.snapshot.sessionEnded) return;
    this.machine.complete();
    const recap = this.buildRecap(this.snapshot.history, this.snapshot.reportCards);
    this.pendingFinish = false;
    this.analytics.track("session_complete", {
      slug: this.snapshot.slug,
      independent: recap.independentCount,
      assisted: recap.assistedCount,
      mode: this.snapshot.mode,
    });
    void this.saveMastery();
    this.patch({
      sessionEnded: true,
      inputLocked: true,
      canUndo: false,
      recap,
      reportCard: null,
      pendingFinish: false,
      engineStatus: reason || "Session complete.",
      liveAnnouncement: reason || `Session complete. ${recap.independentLabel}.`,
    });
    void this.savePractice();
    if (this.snapshot.mode === "branches" && this.learning.scope.branchId) {
      const saved = this.recordBranchEncounter(this.learning.scope.branchId, true);
      this.evidenceWrites.add(saved);
      void saved.finally(() => this.evidenceWrites.delete(saved));
    }
  }

  private idleEngineStatus(): string {
    if (this.snapshot.engineStatus.startsWith("Offline")) return this.snapshot.engineStatus;
    if (this.snapshot.engineStatus.startsWith("Loading")) return this.snapshot.engineStatus;
    if (this.snapshot.engineStatus.startsWith("The engine did not answer"))
      return this.snapshot.engineStatus;
    return "Your move.";
  }

  private rebuildBoard(history: HistoryEntry[]): void {
    this.chess = new Chess();
    const replay = history.length > 0 ? history : [];
    if (replay.length === 0) {
      for (const san of this.prefixSans) {
        if (!tryMove(this.chess, san)) break;
      }
      return;
    }
    for (const entry of replay) {
      if (!tryMove(this.chess, entry.san)) break;
    }
  }

  async gradeReview(
    pass: boolean,
    source: "move" | "button" = "button",
    move?: { played: string; assisted: boolean; alternative?: boolean; note?: string | null }
  ): Promise<void> {
    if (this.snapshot.mode !== "review" || this.reviewBusy || this.snapshot.practiceFeedback)
      return;
    if (source === "button" && (pass || this.snapshot.inputLocked || this.moveInFlight)) return;
    const sessionId = this.machine.sessionId;
    this.reviewBusy = true;
    this.patch({ inputLocked: true });
    try {
      const id = this.reviewCardId;
      const card = id ? await this.review.get(id) : null;
      if (!this.machine.isCurrent({ sessionId })) return;
      if (!card || !id) {
        this.finishSession("Review complete.");
        return;
      }
      const task = this.currentPracticeTask(card.fen);
      if (!task) {
        this.reviewQueue = this.reviewQueue.filter((item) => item !== id);
        await this.showReviewPosition(
          this.reviewQueue[0] || null,
          "This older position is no longer taught. ",
          sessionId,
          null
        );
        return;
      }
      const returning = this.reviewReturns.has(id);
      const outcome = recordAttempt(
        this.learning,
        task,
        move?.played || null,
        pass,
        source === "button" || returning ? "answer" : move?.assisted ? "goal" : "none"
      );
      await this.saveTaskOutcome(task, outcome.result);
      if (!this.machine.isCurrent({ sessionId })) return;
      const remaining = this.reviewQueue.filter((item) => item !== id);
      const repeat = !pass && !returning && this.practiceRepairCount < 4;
      if (repeat) {
        this.practiceRepairCount += 1;
        this.reviewReturns.add(id);
        remaining.splice(Math.min(2, remaining.length), 0, id);
      }
      this.reviewQueue = remaining;
      if (!pass) noteSupport(this.learning, task, "answer");
      const assisted = outcome.result !== "independent";
      const saved = await this.review.get(task.id);
      if (!this.machine.isCurrent({ sessionId })) return;
      const result: ReviewResult = {
        pass,
        played: move?.played || null,
        assisted,
        repaired: outcome.result === "repaired",
        expected: task.expected,
        days: repeat
          ? 0
          : Math.max(1, Math.ceil(((saved?.due || Date.now()) - Date.now()) / 86_400_000)),
        practiceOnly: this.learning.scope.recent,
        alternative: Boolean(move?.alternative),
        note: move?.note || null,
        lesson: this.reviewLesson(card.fen),
      };
      const previous = this.reviewAnswers.findIndex(
        (answer) =>
          answer.expected === result.expected &&
          (answer as ReviewResult & { taskId?: string }).taskId === task.id
      );
      const answer = { ...result, taskId: task.id };
      if (previous >= 0) this.reviewAnswers[previous] = answer;
      else this.reviewAnswers.push(answer);
      this.pendingReviewAdvance = {
        nextId: this.reviewQueue[0] || null,
        againId: repeat ? id : null,
      };
      this.patch({
        practice: practiceSummary(this.learning),
        reviewResult: result,
        reviewRemaining: this.reviewQueue.length,
        practiceFeedback: {
          taskId: task.id,
          title: pass
            ? outcome.result === "repaired"
              ? "Repaired, with the answer already seen"
              : assisted
                ? "Found with support"
                : "Found from memory"
            : "Let’s rebuild this decision",
          expected: task.expected,
          played: result.played,
          result: outcome.result,
          why: result.lesson || `The course teaches ${task.expected} in this position.`,
          comparison:
            !pass && move?.played
              ? move?.note || this.snapshot.coach?.reason || null
              : move?.note || null,
          example: this.course ? practiceExample(this.course, card.fen) : undefined,
          nextLabel: this.pendingReviewAdvance.nextId ? "Next position" : "Finish review",
        },
        inputLocked: true,
        canUndo: false,
        liveAnnouncement: `${reviewResultSentence(result)} ${result.lesson || ""}`,
      });
      this.analytics.track("review_graded", { id, result: outcome.result, mode: "review" });
      await this.savePractice();
    } catch {
      if (this.machine.isCurrent({ sessionId })) {
        const outcome = Object.values(this.learning.outcomes).at(-1);
        if (outcome) {
          const history = this.loadCoursePosition(outcome.task.fen);
          this.machine.notePosition(this.chess.fen(), this.chess.history().length);
          this.patch({ ...this.viewFields(), history, coach: null });
        }
        this.patch({
          inputLocked: false,
          practiceSaveError:
            "This attempt is kept in this tab. Retry saving, then play the position again.",
        });
      }
    } finally {
      if (this.machine.isCurrent({ sessionId })) this.reviewBusy = false;
    }
  }

  private showRecallRepair(): void {
    const task = this.learning.outcomes[this.learning.repairTasks[0]]?.task;
    if (!task) {
      this.finishSession("Recall complete.");
      return;
    }
    const history = this.loadCoursePosition(task.fen);
    this.machine.notePosition(this.chess.fen(), this.chess.history().length);
    this.hintUsedThisTurn = false;
    this.takeBackUsedThisTurn = false;
    this.deepestHintThisTurn = 0;
    this.patch({
      ...this.viewFields(),
      history,
      coach: null,
      practiceFeedback: null,
      recallPrompt: null,
      objective: task.cue,
      hints: [],
      hintRevealed: 0,
      inputLocked: false,
      practiceRepairRemaining: this.learning.repairTasks.length,
      liveAnnouncement: `Return to a difficult decision. ${task.cue}`,
    });
    void this.savePractice();
  }

  /** Feedback stays on its own board until the student chooses to continue. */
  async continuePractice(): Promise<void> {
    if (!this.snapshot.practiceFeedback || this.reviewBusy || this.moveInFlight) return;
    const sessionId = this.machine.sessionId;
    const review = this.pendingReviewAdvance;
    const advance = this.pendingPracticeAdvance;
    this.pendingReviewAdvance = null;
    this.pendingPracticeAdvance = null;
    this.patch({ practiceFeedback: null, inputLocked: true });
    if (review) await this.showReviewPosition(review.nextId, "", sessionId, review.againId);
    else if (advance) await advance();
    else {
      // A reloaded feedback card has no live callback. Its validated game position is sufficient.
      const count = this.learning.completedDecisions;
      if (this.learning.repairing) {
        this.learning.repairTasks.shift();
        if (this.learning.repairTasks.length) this.showRecallRepair();
        else this.finishSession("Recall repairs complete.");
      } else if (this.learning.scope.decisions && count >= this.learning.scope.decisions) {
        const repairs = Object.values(this.learning.outcomes)
          .filter((outcome) => outcome.firstCorrect === false || outcome.answerRevealed)
          .slice(0, 4);
        if (this.snapshot.mode === "recall" && repairs.length) {
          this.learning.repairing = true;
          this.learning.repairTasks = repairs.map((outcome) => outcome.task.id);
          this.showRecallRepair();
        } else this.finishSession("Practice stretch complete.");
      } else {
        if (this.chess.turn() !== this.playerSide)
          await this.computerReply(sessionId, this.machine.turnId);
        if (this.machine.isCurrent({ sessionId }))
          this.patch({ ...this.viewFields(), inputLocked: false });
      }
    }
    if (this.machine.isCurrent({ sessionId })) await this.savePractice();
  }

  revealPracticeAnswer(): void {
    if (this.snapshot.inputLocked || this.snapshot.mode === "learn") return;
    if (this.snapshot.mode === "review") {
      void this.gradeReview(false);
      return;
    }
    const task = this.currentPracticeTask();
    if (!task) return;
    noteSupport(this.learning, task, "answer");
    if (this.snapshot.mode === "recall" || this.learning.scope.challenge) {
      if (this.learning.outcomes[task.id].firstCorrect === null)
        recordAttempt(this.learning, task, null, false, "answer");
      this.saveRevealedOutcome(task);
      this.patch({ practice: practiceSummary(this.learning) });
    }
    this.hintUsedThisTurn = true;
    this.deepestHintThisTurn = 4;
    const note = bookCoachingAtPosition(task.openingId, task.fen, task.expected);
    const text = `Play ${task.expected}. ${note?.why || "Then compare how the pieces work together."}`;
    this.patch({ recallPrompt: text, objective: text, liveAnnouncement: text });
    void this.savePractice();
  }

  /** Load the next Repeat positions card, or end the sitting when the queue is empty. */
  private reviewCardIsSupported(card: MasteryRecord): boolean {
    try {
      return (
        card.openingId === this.course?.openingId &&
        Boolean(this.currentPracticeTask(card.fen)) &&
        new Chess(card.fen).turn() === this.playerSide
      );
    } catch {
      return false;
    }
  }

  private async showReviewPosition(
    nextId: string | null,
    resultText: string,
    sessionId: string,
    againId: string | null
  ): Promise<void> {
    this.reviewCardId = nextId;
    if (!nextId) {
      this.finishSession(`${resultText}Review queue complete.`);
      return;
    }
    const nextCard = await this.review.get(nextId);
    if (!this.machine.isCurrent({ sessionId })) return;
    if (!nextCard) {
      this.finishSession(`${resultText}Review queue complete.`);
      return;
    }
    await this.adoptReviewCourse(nextCard);
    if (!this.machine.isCurrent({ sessionId })) return;
    if (!this.reviewCardIsSupported(nextCard)) {
      this.reviewQueue = this.reviewQueue.filter((id) => id !== nextId);
      await this.showReviewPosition(
        this.reviewQueue[0] || null,
        "An older unsupported question was skipped. ",
        sessionId,
        null
      );
      return;
    }
    const setup = this.loadCoursePosition(nextCard.fen);
    if (positionKey(this.chess.fen()) !== positionKey(nextCard.fen)) {
      this.finishSession(`${resultText}Review queue complete.`);
      return;
    }
    this.machine.notePosition(this.chess.fen(), this.chess.history().length);
    this.hintUsedThisTurn = false;
    this.takeBackUsedThisTurn = false;
    this.deepestHintThisTurn = 0;
    const again = againId !== null && nextId === againId;
    this.patch({
      ...this.viewFields(),
      history: setup,
      coach: null,
      hints: [],
      hintRevealed: 0,
      recap: null,
      sessionEnded: false,
      inputLocked: false,
      reviewRemaining: this.reviewQueue.length,
      reviewResult: null,
      practiceFeedback: null,
      liveAnnouncement: again
        ? `${resultText}Play it once more.`
        : `${resultText}Next review position. ${this.playerName} to move.`,
    });
  }

  /** The next course lessons from a position, following the course's replies. */
  private upcomingLessons(openingId: string | undefined, fen: string): UpcomingLesson[] {
    const course = this.course;
    if (!course) return [];
    const chess = new Chess(fen);
    const lessons: UpcomingLesson[] = [];
    while (lessons.length < REPORT_CARD_EVERY && chess.turn() === this.playerSide) {
      const note = bookCoachingAtPosition(openingId, chess.fen());
      if (!note || !tryMove(chess, note.san)) break;
      lessons.push({
        moveNumber: note.moveNumber,
        san: note.san,
        goal: note.goal,
        watch: note.watch,
      });
      const reply = replyFromGraph(course, chess.fen());
      if (!reply || !tryMove(chess, reply)) break;
    }
    return lessons;
  }

  private latestMedalSentence(): string {
    const student = [...this.snapshot.history].reverse().find((entry) => entry.byStudent);
    return student
      ? [medalLiveText(student), describeEngineChoices(student.san, student.engineChoices)]
          .filter(Boolean)
          .join(" ")
      : "";
  }

  private fallbackRanker(): RankerLike | null {
    if (this.ranker === undefined) {
      try {
        this.ranker = this.createRanker();
      } catch {
        this.ranker = null;
      }
    }
    return this.ranker;
  }

  private rankingTasks = new Set<Promise<void>>();

  private trackRanking(task: Promise<void>): void {
    this.rankingTasks.add(task);
    void task.finally(() => this.rankingTasks.delete(task));
  }

  /** Resolves once every in-flight ranking has settled (used by tests). */
  rankingSettled(): Promise<void> {
    return Promise.all([...this.rankingTasks]).then(() => undefined);
  }

  /**
   * Apply a ranking to the move it was computed for. The entry is matched by
   * ply, move and position so a late result still lands after the student
   * has played on; a take-back or a new session makes it vanish instead.
   */
  /** The badge and engine verdict for a move, from the ranking's own scores. */
  private gradeFromRanking(
    entry: HistoryEntry,
    loss: number | null
  ): Pick<HistoryEntry, "qualityBand" | "assessment"> {
    const assessment = entry.assessment;
    if (loss == null || !assessment) return {};
    const courseGrade = entry.isMainline && loss < 200;
    const band: MoveAssessment["engine"]["band"] =
      courseGrade || loss <= 15
        ? "top"
        : loss <= 40
          ? "equivalent"
          : loss <= 90
            ? "playable"
            : "inferior";
    const tactical: MoveAssessment["tactical"]["status"] = courseGrade
      ? "safe"
      : loss >= 200
        ? "losing"
        : loss >= 90
          ? "questionable"
          : "safe";
    const next: MoveAssessment = {
      ...assessment,
      engine: { ...assessment.engine, band },
      tactical: { ...assessment.tactical, status: tactical, cpLoss: loss },
    };
    return { qualityBand: qualityBandFromAssessment(next), assessment: next };
  }

  private applyRanking(
    args: {
      sessionId: string;
      turnId: string;
      ply: number;
      uci: string;
      fenBefore: string;
      assisted: boolean;
      withheld: WithheldReason;
    },
    lines: RankedLine[],
    legalCount: number
  ): boolean {
    if (!this.machine.isCurrent({ sessionId: args.sessionId })) return false;
    const target = this.snapshot.history.find(
      (entry) =>
        entry.byStudent &&
        entry.turnId === args.turnId &&
        entry.ply === args.ply &&
        entry.uci === args.uci &&
        entry.fenBefore === args.fenBefore
    );
    if (!target) return false;
    const decision = decideMedal({
      uci: args.uci,
      lines,
      assisted: args.assisted,
      withheld: args.withheld,
      legalCount,
    });
    // The ranking scores every move in one search, so its verdict replaces
    // the first estimate (two separate searches) and the badge can no longer
    // contradict the medal. A course move keeps its course grade.
    const loss = lossFromLines(lines, args.uci);
    const graded = this.gradeFromRanking(target, loss);
    rememberEngineLines(args.fenBefore, lines, legalCount);
    const playedLine = lines.find((line) => line.uci === args.uci);
    const playedScore = playedLine
      ? parseEngineScore({ scoreCp: playedLine.scoreCp ?? undefined, mate: playedLine.mate })
      : null;
    const bestLine = rankLines(lines)[0];
    const bestMove = bestLine
      ? tryMove(new Chess(args.fenBefore), {
          from: bestLine.uci.slice(0, 2),
          to: bestLine.uci.slice(2, 4),
          ...(bestLine.uci.length > 4 ? { promotion: bestLine.uci[4] } : {}),
        })
      : null;
    const engineBestSan = bestMove && bestMove.san !== target.san ? bestMove.san : null;
    const engineChoices = topEngineChoices(args.fenBefore, lines);
    const engineChoiceText = describeEngineChoices(target.san, engineChoices, loss);
    const evalAfterCp = playedScore
      ? scoreToComparable(toWhitePov(playedScore, new Chess(args.fenBefore).turn()))
      : target.evalAfterCp;
    let reason = target.reason || "";
    let positionReason = target.positionReason;
    let consequenceReason = target.consequenceReason;
    if (graded.assessment) {
      const move = tryMove(new Chess(args.fenBefore), target.san);
      if (move && positionReason) {
        const updated = explainOffBookMove(
          args.fenBefore,
          move,
          whiteMoveNumber(target.ply) <= 2,
          loss
        );
        reason = reason.replace(positionReason, updated);
        positionReason = updated;
      }
      if (move && consequenceReason) {
        const updated = moveConsequence(args.fenBefore, move, loss);
        reason = reason.replace(consequenceReason, updated);
        consequenceReason = updated;
      }
      reason = reason
        .replace(
          boundaryEnginePhrase(target.assessment?.engine.band),
          boundaryEnginePhrase(graded.assessment.engine.band)
        )
        .trim();
    }
    const history = this.snapshot.history.map((entry) =>
      entry === target
        ? {
            ...entry,
            ...graded,
            reason,
            positionReason,
            consequenceReason,
            message:
              loss == null
                ? entry.message
                : entry.message?.replace(
                    /Stockfish sees about \d+ cp lost\./,
                    loss > 0
                      ? `Stockfish sees about ${Math.round(loss)} cp lost.`
                      : "The engine ranking finds no loss against its top move."
                  ),
            ...(playedLine ? { evalAfterCp, engineBestSan } : {}),
            ...(engineChoices.length > 1 ? { engineChoices } : {}),
            medal: decision.medal,
            earnedMedal: decision.earnedMedal,
            rank: decision.rank,
            medalStatus: decision.medalStatus,
            withheld: decision.withheld,
            moveScore: decision.moveScore,
            outsideTop: decision.outsideTop,
            total: decision.total,
            rankedCount: decision.rankedCount,
            percentile: decision.percentile,
          }
        : entry
    );
    const reportCards = this.snapshot.reportCards.map((card) => {
      const input = this.reportCardInputs.get(card.windowIndex);
      const moveNumber = whiteMoveNumber(target.ply);
      if (!input || moveNumber < card.fromMove || moveNumber > card.toMove) return card;
      const refreshedInput = {
        ...input,
        moves: input.moves.map((move) =>
          move.moveNumber === moveNumber
            ? {
                ...move,
                qualityBand: graded.qualityBand ?? move.qualityBand,
                cpLoss: graded.assessment?.tactical.cpLoss ?? move.cpLoss,
                reason,
                ...(playedLine ? { evalAfterCp: evalAfterCp ?? null, engineBestSan } : {}),
                ...(engineChoices.length > 1 ? { engineChoices } : {}),
                medal: decision.medal,
                earnedMedal: decision.earnedMedal,
                rank: decision.rank,
                rankTotal: decision.total,
                percentile: decision.percentile,
              }
            : move
        ),
      };
      refreshedInput.engineChecked = refreshedInput.moves.some(
        (m) => m.rank != null || m.evalAfterCp != null
      );
      this.reportCardInputs.set(card.windowIndex, refreshedInput);
      const refreshed = buildReportCard(refreshedInput);
      appendReportCardLog({
        ...summarizeReportCard(refreshed, input.openingId),
        key: `${args.sessionId}:${card.windowIndex}`,
      });
      return refreshed;
    });
    const coach = this.snapshot.coach;
    const sameCard =
      coach && coach.san === target.san && coach.moveNumber === whiteMoveNumber(target.ply);
    // Screen readers hear the medal once the ranking is in, for the latest move.
    const latest = [...history].reverse().find((entry) => entry.byStudent);
    // (A report card or the end of the session speaks for itself instead.)
    const ranked =
      latest &&
      latest.turnId === target.turnId &&
      latest.medalStatus === "verified" &&
      !this.snapshot.reportCard &&
      !this.snapshot.sessionEnded
        ? [medalLiveText(latest), describeEngineChoices(latest.san, latest.engineChoices, loss)]
            .filter(Boolean)
            .join(" ")
        : "";
    // Appended, so a turn prompt not yet read out is not cut off.
    const announced = this.snapshot.liveAnnouncement.replace(
      / ?Ranking \S+ against every legal move\./,
      ""
    );
    this.patch({
      history,
      reportCards,
      ...(ranked ? { liveAnnouncement: `${announced} ${ranked}`.trim() } : {}),
      ...(sameCard
        ? {
            coach: {
              ...coach,
              ...(graded.qualityBand && graded.assessment
                ? {
                    judgment: graded.qualityBand,
                    engineBand: engineBandLabel(graded.assessment.engine.band),
                    tacticalStatus: graded.assessment.tactical.status,
                    axesLine: formatAssessmentAxes(graded.assessment),
                    reason,
                  }
                : {}),
              ...(engineChoiceText ? { engineChoiceText } : {}),
            },
          }
        : {}),
      reportCard: this.snapshot.reportCard
        ? reportCards.find((card) => card.id === this.snapshot.reportCard?.id) || null
        : null,
      recap: this.snapshot.recap ? this.buildRecap(history, reportCards) : null,
    });
    if (graded.assessment) void this.saveMastery();
    return true;
  }

  /**
   * Rank the played move against every legal move in the position. The main
   * engine is asked for one line per legal move. If it answers with a partial
   * list (an older server caps MultiPV at five) and the move is not in it,
   * the dedicated ranker worker completes the ranking so the scorecard can
   * say "6th of 24" rather than "beyond 5th". An engine that fails outright
   * leaves the rank unavailable; no second engine is spun up for that.
   */
  private async attachMedal(args: {
    sessionId: string;
    turnId: string;
    ply: number;
    uci: string;
    san: string;
    fenBefore: string;
    assisted: boolean;
    withheld: WithheldReason;
  }): Promise<void> {
    if (!this.engine || !this.machine.isCurrent({ sessionId: args.sessionId })) return;
    const legalCount = new Chess(args.fenBefore).moves().length;
    // The position may already have been scored while the student thought.
    let lines: RankedLine[] = (await this.positionLines.get(args.fenBefore)) || [];
    if (!lines.length) {
      try {
        const res = await this.engine.analyze(args.fenBefore, {
          depth: MEDAL_DEPTH,
          multipv: Math.min(legalCount, RANK_MAX_LINES),
        });
        lines = legalLines(args.fenBefore, linesFromCandidates(res.candidates));
      } catch {
        lines = [];
      }
    }
    if (!this.applyRanking(args, lines, legalCount)) return;
    const partial =
      lines.length >= 2 &&
      lines.length < Math.min(legalCount, RANK_MAX_LINES) &&
      !lines.some((line) => line.uci === args.uci);
    if (!partial) return;
    const ranker = this.fallbackRanker();
    if (!ranker) return;
    let rows: RankedLine[] = [];
    try {
      rows = linesFromCandidates(
        await ranker.rankAll({ fen: args.fenBefore, legalCount, depth: MEDAL_DEPTH })
      );
    } catch {
      return;
    }
    if (rows.length > lines.length) this.applyRanking(args, rows, legalCount);
  }

  /**
   * Save this session's positions as they stand. Runs after every move, after
   * a take-back and at the end of a session; saves run one after another.
   * Repeat positions saves each answer itself, in gradeReview. Side lines
   * does not grade: exploring a reply must not lapse the card.
   */
  private saveMastery(): Promise<void> {
    const course = this.course;
    if (!course || this.snapshot.mode === "review" || this.snapshot.mode === "branches")
      return this.masteryFlush;
    const history = this.snapshot.history.filter(
      (entry) => !entry.turnId || !this.masteryIgnored.has(entry.turnId)
    );
    const before = this.masteryBefore;
    const grades = this.masteryGrades;
    const epoch = this.masteryEpoch;
    const learningId = this.learning.id;
    const generation = this.storeGeneration;
    this.masteryFlush = this.masteryFlush.then(() =>
      this.syncMastery(course, history, before, grades, epoch, learningId, generation)
    );
    return this.masteryFlush;
  }

  private async syncMastery(
    course: LessonCourse,
    history: HistoryEntry[],
    before: Map<string, MasteryRecord | null>,
    grades: Map<string, number>,
    epoch: number,
    learningId: string,
    generation: number | undefined
  ): Promise<void> {
    // Progress was reset or imported since this save was queued.
    const stale = () => epoch !== this.masteryEpoch;
    const played = new Map<string, HistoryEntry>();
    for (const entry of history) {
      if (!entry.byStudent || entry.setup) continue;
      played.set(`${course.id}:${positionKey(entry.fenBefore)}`, entry);
    }
    try {
      for (const [id, entry] of played) {
        if (stale()) return;
        const key = positionKey(entry.fenBefore);
        const existing = await this.review.get(id);
        const playedCourse = ["repertoire", "transposition"].includes(
          entry.assessment?.book.status || ""
        );
        const answer = entry.courseSan || entry.recommendedSan || (playedCourse ? entry.san : null);
        if (!answer) continue;
        const outcomeId = `${learningId}:${id}`;
        if (existing?.lastOutcomeId === outcomeId) continue;
        const record =
          existing ||
          newMasteryRecord({
            courseId: course.id,
            openingId: course.openingId,
            positionKey: key,
            fen: entry.fenBefore,
            recommendedSan: answer,
          });
        if (!before.has(id)) before.set(id, existing);
        const result: LearningResult =
          entry.mode === "learn" || !entry.mode
            ? "studied"
            : entry.practiceResult ||
              (entry.assisted ? "cued" : playedCourse ? "independent" : "missed");
        const next = {
          ...record,
          ...scheduleLearning(record, result),
          lastOutcomeId: outcomeId,
          recommendedSan: answer,
          contentVersion: course.contentVersion,
          ...(result === "independent" &&
          (!record.lastRetrievedAt || Date.now() - record.lastRetrievedAt >= 72_000_000)
            ? { lastRetrievedAt: Date.now(), independentReps: (record.independentReps || 0) + 1 }
            : {}),
        };
        if (stale()) return;
        const saved = this.review.putIfCurrent
          ? await this.review.putIfCurrent(next, record.revision || 0, generation)
          : (await this.review.put(next), true);
        if (!saved) this.patch({ practiceSaveError: "Newer progress from another tab was kept." });
        grades.set(id, next.lastGrade);
      }
    } catch {
      this.patch({
        practiceSaveError:
          "Your game is safe, but progress could not be saved. Keep this tab open.",
      });
    }
  }

  flushMastery(): Promise<void> {
    return Promise.all([this.masteryFlush, this.practiceFlush, ...this.evidenceWrites]).then(
      () => {}
    );
  }

  repeatLinePrefix(): string[] {
    return [];
  }

  repeatWeakPrefix(): string[] {
    const recap = this.snapshot.recap;
    if (!recap?.onePosition) return [];
    const target = recap.onePosition.fenBefore;
    if (this.course) return pathToPosition(this.course, positionKey(target)) || [];
    const prefix: string[] = [];
    const probe = new Chess();
    for (const entry of this.snapshot.history) {
      if (probe.fen() === target) break;
      const moved = probe.move(entry.san);
      if (!moved) break;
      prefix.push(entry.san);
    }
    return prefix;
  }
}

let singleton: TrainerSession | null = null;

export function getTrainer(deps?: TrainerDeps): TrainerSession {
  if (!singleton) singleton = new TrainerSession(deps);
  return singleton;
}

export function resetTrainer(deps?: TrainerDeps): TrainerSession {
  singleton = new TrainerSession(deps);
  return singleton;
}
